import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { decomposeDisplay, SEG_PATTERNS } from "./display";
import { RAIL_TIMBRES, RailTimbre, Timbre, effectivePitchIndex, isTimbreId, pitchIndexToMidi } from "./audio";
import { track } from "./telemetry";
import {
  AUDIO_BASE_MIDI,
  CAP_Z_OFFSET,
  COLLISION_RADIUS,
  ENVELOPE_ATTACK_MAX_S,
  ENVELOPE_DECAY_MAX_S,
  ENVELOPE_RELEASE_MAX_S,
  DECAY_DEFAULT_S,
  DELAY_DEFAULT_S,
  ROUTER_HOLD_DEFAULT_S,
  DEFAULT_BOX_HALF,
  DEFAULT_FRICTION,
  DEFAULT_MASS,
  DEFAULT_RESTITUTION,
  DEFAULT_PITCH_INDEX,
  DEFAULT_FIELD_FADE,
  DEFAULT_FIELD_K,
  DEFAULT_FIELD_MODE,
  DEFAULT_FIELD_OMEGA,
  DEFAULT_FIELD_RADIUS,
  DEFAULT_FUNNEL,
  DEFAULT_SPHERE_RADIUS,
  f32,
  FORCE_COLOR,
  GRID_CELL_SIZE,
  GRID_IDX_TEX_W,
  GRID_LARGE_SPAN_CELLS,
  GRID_MAX_DIM,
  KEY_COLOR_BLACK,
  KEY_COLOR_WHITE,
  MAX_OBJECTS,
  METAL_METALNESS,
  METAL_ROUGHNESS,
  OBJECT_METALNESS,
  OBJECT_ROUGHNESS,
  PARTICLE_WORLD_SIZE,
  PITCH_LIGHTNESS_MAX,
  PITCH_LIGHTNESS_MIN,
  PITCH_OCTAVE_MAX,
  PITCH_OCTAVE_MIN,
  PITCH_SATURATION,
  PREVIEW_COLOR,
  RAIL_GAP_RATIO,
  RAIL_SAMPLES,
  RAIL_SEGMENTS,
  LAMP_DEFAULT_HUE,
  LAMP_DEFAULT_SAT,
  BLOOM_THRESHOLD,
  LAMP_BLOOM_LUMA_MARGIN,
  LAMP_PEAK_MAX,
  LAMP_PEAK_MIN,
  RAIL_TUBE_FACTOR,
  RAIL_TUBE_MIN,
  KNOB_DEFAULT_VALUE,
  KNOB_MIN_DEFAULT,
  KNOB_MAX_DEFAULT,
  KNOB_INT_DEFAULT_MAX,
} from "./constants";

/**
 * Shared world-space cut plane for the 2D section view, applied only to the
 * funnel: it keeps the z<=0 half and clips the camera-facing (z>0) shell so the
 * FrontSide shell shows only its outer silhouette while the section cap closes
 * the cut opening, hiding the far inner wall. Sphere/box skip the clip and cap
 * entirely, drawing their full rounded body in place. Pushing `constant` far
 * positive (3D / tween) keeps everything, disabling the cut.
 */
const SECTION_PLANE = new THREE.Plane(new THREE.Vector3(0, 0, -1), 0);
const SECTION_CLIP_OFF = 1e6;

export type ObjectType = "box" | "sphere" | "funnel" | "force" | "rail" | "calc" | "count" | "display" | "timer" | "decay" | "orbit" | "group" | "timbre" | "pitchQuantize" | "delay" | "router" | "button" | "lamp" | "knob" | "envelope" | "array" | "curve" | "global";
export type ColliderType = "box" | "sphere" | "funnel" | "force" | "rail";

/**
 * What a timer node emits on its valueOut: `time` is the elapsed seconds since
 * placement (sim clock), `loop` is the performance-cycle phase in [0,1) — one
 * full realignment of all placed sequencer patterns.
 */
export type TimerMode = "time" | "loop";

/**
 * How a button widget reacts to a press. `push` is momentary (valueOut 1 while
 * held), `toggle` latches — each press flips the on/off state valueOut follows.
 * Both modes expose valueOut and pulseOut at all times: push fires one pulseOut
 * on the press-down, toggle fires one only on the off→on flip.
 */
export type ButtonMode = "push" | "toggle";

export const COLOR_SETS = ["pitch", "keyboard", "mono"] as const;
export type ColorSet = (typeof COLOR_SETS)[number];

export const OBJECT_MATERIALS = ["matte", "metal", "glossy", "glass"] as const;
export type ObjectMaterial = (typeof OBJECT_MATERIALS)[number];

/**
 * The single operation a calc node performs. The binary operators combine the
 * node's two value inputs (`a operator b`); the unary operators transform the
 * single input. `random` is neither: it takes no value operand but a pulse-in,
 * and emits a fresh 0-1 value on each pulse (held until the next), so it exposes
 * a pulse-in instead of operand-ins and is resolved from runtime state rather
 * than its inputs. The kind selects which ports the node exposes and how the
 * evaluator resolves it. (The pulse accumulator is the separate `count` node.)
 */
export type ValueOperator =
  | "add" | "sub" | "mul" | "div" | "mod"
  | "floor" | "frac" | "round" | "ceil"
  | "abs" | "neg" | "sign"
  | "sqrt" | "square" | "recip"
  | "sin" | "cos" | "random";

export type ValueOperatorKind = "binary" | "unary" | "random";

export const VALUE_OPERATORS: readonly ValueOperator[] = [
  "add", "sub", "mul", "div", "mod",
  "floor", "frac", "round", "ceil",
  "abs", "neg", "sign",
  "sqrt", "square", "recip",
  "sin", "cos", "random",
];

const BINARY_VALUE_OPERATORS: readonly ValueOperator[] = ["add", "sub", "mul", "div", "mod"];

export function valueOperatorKind(operator: ValueOperator): ValueOperatorKind {
  if (operator === "random") {
    return "random";
  }
  return BINARY_VALUE_OPERATORS.includes(operator) ? "binary" : "unary";
}

const TYPE_CODE: Record<ColliderType, number> = { box: 0, sphere: 1, funnel: 2, force: 3, rail: 4 };

const ALLOWED_TYPES: readonly ObjectType[] = ["box", "sphere", "funnel", "force", "rail", "calc", "count", "display", "timer", "decay", "orbit", "group", "timbre", "pitchQuantize", "delay", "router", "button", "lamp", "knob", "envelope", "array", "curve", "global"];

export function isObjectType(value: string): value is ObjectType {
  return (ALLOWED_TYPES as readonly string[]).includes(value);
}

/**
 * Whether a type takes part in SDF collision packing (`uObjTex`/`uObjCount`).
 * Value / orbit / group / timbre are non-collider node objects: they ride the same
 * placement / selection / panel / persistence machinery but are excluded from
 * the collision uniforms, so the contact buffer index stays a dense collider-only
 * sequence. Force packs (it drives the field accel) but its SDF returns 1e9, so it
 * never registers contacts.
 */
export function isCollider(type: ObjectType): boolean {
  return type === "box" || type === "sphere" || type === "funnel" || type === "force" || type === "rail";
}

/**
 * Two-layer transform of an orbit node. The parent layer rotates a connected
 * object about the orbit's placed position (the pivot) at `rotSpeed` (deg/sec
 * per XYZ axis, integrated over the sim clock so equal-speed orbits stay phase
 * locked). The child layer adds a per-local-axis sine translation of amplitude
 * `amp`. `clockSync` selects how `rotSpeed` and `periodPct` are read: when on,
 * everything is expressed relative to the room clock — `rotSpeed` is revolutions
 * per clock period (1.0 = one turn per period) and `periodPct` is a fraction of
 * the period — so synced orbits phase-lock to the tempo and to each other. When
 * off the orbit free-runs: `rotSpeed` is degrees/second and `periodPct` is the
 * child period in absolute seconds. `phasePct` is a fraction of a cycle in both.
 * Held only on `orbit`-typed objects; zeros on every other.
 */
export interface OrbitParams {
  rotSpeed: THREE.Vector3;
  amp: THREE.Vector3;
  periodPct: THREE.Vector3;
  phasePct: THREE.Vector3;
  clockSync: boolean;
  play: boolean;
}

export function defaultOrbitParams(): OrbitParams {
  return {
    rotSpeed: new THREE.Vector3(0, 0, 30),
    amp: new THREE.Vector3(0, 0, 0),
    periodPct: new THREE.Vector3(0.1, 1, 1),
    phasePct: new THREE.Vector3(0, 0, 0),
    clockSync: false,
    play: true,
  };
}

export function cloneOrbitParams(o: OrbitParams): OrbitParams {
  return {
    rotSpeed: o.rotSpeed.clone(),
    amp: o.amp.clone(),
    periodPct: o.periodPct.clone(),
    phasePct: o.phasePct.clone(),
    clockSync: o.clockSync,
    play: o.play,
  };
}

function vec3Equals(a: THREE.Vector3, b: THREE.Vector3): boolean {
  return a.x === b.x && a.y === b.y && a.z === b.z;
}

/** True when an orbit carries every `defaultOrbitParams()` value, so the serializer can drop the whole block. */
function orbitIsDefault(o: OrbitParams): boolean {
  const d = defaultOrbitParams();
  return (
    vec3Equals(o.rotSpeed, d.rotSpeed) &&
    vec3Equals(o.amp, d.amp) &&
    vec3Equals(o.periodPct, d.periodPct) &&
    vec3Equals(o.phasePct, d.phasePct) &&
    o.clockSync === d.clockSync &&
    o.play === d.play
  );
}

function parseRailPoints(raw: unknown): THREE.Vector2[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const points: THREE.Vector2[] = [];
  for (const entry of raw) {
    if (!Array.isArray(entry) || entry.length < 2) {
      return [];
    }
    const x = num(entry[0]);
    const y = num(entry[1]);
    if (x === null || y === null) {
      return [];
    }
    points.push(new THREE.Vector2(x, y));
  }
  return points;
}

function parseVec3(v: unknown, fallback: THREE.Vector3): THREE.Vector3 {
  if (!Array.isArray(v) || v.length < 3) {
    return fallback.clone();
  }
  const x = num(v[0]);
  const y = num(v[1]);
  const z = num(v[2]);
  return x === null || y === null || z === null ? fallback.clone() : new THREE.Vector3(x, y, z);
}

function parseOrbitParams(raw: unknown): OrbitParams {
  const d = defaultOrbitParams();
  if (typeof raw !== "object" || raw === null) {
    return d;
  }
  const e = raw as Record<string, unknown>;
  return {
    rotSpeed: parseVec3(e.rotSpeed, d.rotSpeed),
    amp: parseVec3(e.amp, d.amp),
    periodPct: parseVec3(e.periodPct, d.periodPct),
    phasePct: parseVec3(e.phasePct, d.phasePct),
    clockSync: typeof e.clockSync === "boolean" ? e.clockSync : d.clockSync,
    play: typeof e.play === "boolean" ? e.play : true,
  };
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

export const ARRAY_DEFAULT_MIN = 0;
export const ARRAY_DEFAULT_MAX = 7;
export const ARRAY_DEFAULT_LEN = 16;
export const ARRAY_LEN_MAX = 32;
export const ARRAY_ROWS_MAX = 64;
export const CURVE_DEFAULT_MIN = 0;
export const CURVE_DEFAULT_MAX = 1;
export const CURVE_DEFAULT_LEN = 256;
export const CURVE_LEN_MAX = 256;
/**
 * Default `seqCycleLength` for a curve: how many stepIn pulses complete one pass
 * over its 256 samples. The curve's resolution is fixed at CURVE_DEFAULT_LEN, so
 * the cycle length is the independent "speed" control (smoothness ≠ period).
 */
export const CURVE_DEFAULT_CYCLE = 16;
export const SEQ_LEN_MIN = 1;

/** A 0→peak→0 triangle of `len` samples (peak at the midpoint); array cells round to ints. */
function mountainSeq(len: number, peak: number, integer: boolean): number[] {
  const out = new Array<number>(len);
  for (let i = 0; i < len; i++) {
    const t = len <= 1 ? 0 : i / (len - 1);
    const v = peak * (1 - Math.abs(2 * t - 1));
    out[i] = integer ? Math.round(v) : v;
  }
  return out;
}

/**
 * The per-type seq* defaults: array is an integer step grid, curve a longer float strip.
 * curve/array seed a 0→max→0 mountain; other types keep a flat min strip so their
 * unused seq fields stay all-min and serialize omits them.
 */
export function defaultSeq(type: ObjectType): { seqMin: number; seqMax: number; seqLen: number; seqValues: number[] } {
  if (type === "curve") {
    return { seqMin: CURVE_DEFAULT_MIN, seqMax: CURVE_DEFAULT_MAX, seqLen: CURVE_DEFAULT_LEN, seqValues: mountainSeq(CURVE_DEFAULT_LEN, CURVE_DEFAULT_MAX, false) };
  }
  if (type === "array") {
    return { seqMin: ARRAY_DEFAULT_MIN, seqMax: ARRAY_DEFAULT_MAX, seqLen: ARRAY_DEFAULT_LEN, seqValues: mountainSeq(ARRAY_DEFAULT_LEN, ARRAY_DEFAULT_MAX, true) };
  }
  return { seqMin: ARRAY_DEFAULT_MIN, seqMax: ARRAY_DEFAULT_MAX, seqLen: ARRAY_DEFAULT_LEN, seqValues: new Array<number>(ARRAY_DEFAULT_LEN).fill(ARRAY_DEFAULT_MIN) };
}

/**
 * Coerces one seq cell value into the node's range: clamped to [min,max], and
 * rounded to an integer for an array (a curve keeps the float). Out-of-range and
 * non-finite values fold to `min` rather than NaN.
 */
export function coerceSeqValue(type: ObjectType, value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) {
    return min;
  }
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  const clamped = Math.min(Math.max(value, lo), hi);
  return type === "array" ? Math.round(clamped) : clamped;
}

/**
 * Restores a seq value array to exactly `len` cells, coercing each present cell
 * into range and filling absent trailing cells with `min` so a stored array
 * survives a seqLen change. A wholly absent field rebuilds the per-type default
 * shape — the 0→max→0 mountain for curve/array, a flat `min` strip otherwise —
 * because the serializer omits seqValues that equal the default; without the
 * mountain reconstruction an omitted default curve/array (e.g. a just-placed
 * one's first sync op, which carries no seqValues) would rebuild flat.
 */
function parseSeqValues(raw: unknown, type: ObjectType, len: number, min: number, max: number): number[] {
  if (!Array.isArray(raw)) {
    return type === "curve" || type === "array"
      ? mountainSeq(len, max, type === "array")
      : new Array<number>(len).fill(coerceSeqValue(type, min, min, max));
  }
  const out = new Array<number>(len).fill(coerceSeqValue(type, min, min, max));
  for (let i = 0; i < len && i < raw.length; i++) {
    const v = num(raw[i]);
    if (v !== null) {
      out[i] = coerceSeqValue(type, v, min, max);
    }
  }
  return out;
}

/**
 * Restores a timbre node's governed-timbre set. A present array (even empty, =
 * "governs nothing") is honored after dropping unknown ids; an absent field is a
 * legacy single-select scene, restored as `[fallback]`. Non-timbre types never
 * use the set, so they collapse to the representative.
 */
function parseTimbreIds(raw: unknown, type: ObjectType, fallback: Timbre): Timbre[] {
  if (type !== "timbre") {
    return [fallback];
  }
  if (Array.isArray(raw)) {
    return raw.filter((x: unknown): x is Timbre => typeof x === "string" && isTimbreId(x));
  }
  return [fallback];
}

function seqValuesEqual(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

export const WIDGET_DEFAULT_SCALE = 1;
export const WIDGET_SCALE_MIN = 0.3;
export const WIDGET_SCALE_MAX = 5;

/** All 12 semitone bits set: a fully-open PitchQuantize mask (every pitch class allowed = chromatic). */
export const FULL_SCALE_MASK = 0xfff;

/** Pitch classes 0,2,4,7,9 as a 12-bit mask: the default PitchQuantize mask (major pentatonic). */
export const PENTATONIC_SCALE_MASK = 0b001010010101;

/**
 * The PitchQuantize node's 12-bit scale mask read off params.x: bit i (0..11) on
 * means pitch class i is allowed. Clamped to the valid 0..4095 range and rounded,
 * since the value rides the float params slot.
 */
export function pitchQuantizeMask(obj: PlacedObject): number {
  return Math.min(Math.max(Math.round(obj.params.x), 0), FULL_SCALE_MASK);
}

/**
 * Uniform scale factor for a button / lamp widget, read off params.x and
 * clamped to a sane range. Scenes saved before widgets carried a scale stored 0
 * (the old empty Vector4), so a non-positive value promotes to 1 to keep legacy
 * widgets at their original size.
 */
export function widgetScale(obj: PlacedObject): number {
  const s = obj.params.x;
  if (s <= 0) {
    return WIDGET_DEFAULT_SCALE;
  }
  return Math.min(Math.max(s, WIDGET_SCALE_MIN), WIDGET_SCALE_MAX);
}

/**
 * Canonical per-type defaults for one placed object, the single source the
 * serializer omits against and the parser fills absent fields from, so a
 * dropped key round-trips back to the same value. Position is origin / identity
 * and id is empty (always written explicitly, never omitted). `restitution` /
 * `friction` carry the collider default even on non-colliders / rail (rail
 * pins them to 0 on parse regardless), keeping the table type-agnostic where
 * the value is inert.
 */
function defaultPlaced(type: ObjectType): PlacedObject {
  return {
    id: "",
    type,
    position: new THREE.Vector3(),
    rotation: new THREE.Quaternion(),
    restitution: DEFAULT_RESTITUTION,
    friction: DEFAULT_FRICTION,
    mass: DEFAULT_MASS,
    massEnabled: false,
    killOnContact: false,
    collisionEnabled: true,
    pitchIndex: DEFAULT_PITCH_INDEX,
    timbreId: "tone",
    timbreIds: ["tone"],
    volume: 1,
    muted: false,
    fade: DEFAULT_FIELD_FADE,
    operator: "add",
    operand: 0,
    decayS: DECAY_DEFAULT_S,
    delayS: DELAY_DEFAULT_S,
    routerHoldS: ROUTER_HOLD_DEFAULT_S,
    routerHoldResetOnPulse: false,
    timerMode: "time",
    knobValue: KNOB_DEFAULT_VALUE,
    knobMin: KNOB_MIN_DEFAULT,
    knobMax: KNOB_MAX_DEFAULT,
    knobInt: false,
    buttonMode: "push",
    buttonOn: false,
    buttonPulseOnReset: false,
    lampHue: LAMP_DEFAULT_HUE,
    lampSat: LAMP_DEFAULT_SAT,
    lampValue: 0,
    ...defaultSeq(type),
    seqCycleLength: CURVE_DEFAULT_CYCLE,
    params: defaultParams(type),
    scale: new THREE.Vector2(1, 1),
    arcDeg: 360,
    orbit: defaultOrbitParams(),
    railPoints: [],
    pitchIndexEnd: DEFAULT_PITCH_INDEX,
    railTimbre: "sine",
    name: "",
  };
}

/**
 * Restores one placed object from its serialized form, supplying per-type
 * defaults for every absent optional field — the serializer drops any field
 * equal to these defaults (per-type whitelist + default omission), so a missing
 * key means "default for this type" rather than malformed data. An unknown calc
 * operator falls back to `add`. A rail's restitution / friction
 * are design-fixed at 0 (glide groove, no physics sliders in the panel), so
 * stored values are overridden rather than honored. Only `type` and a finite
 * x/y/z position are required; a rail additionally needs ≥2 control points.
 */
function parsePlaced(entry: unknown): PlacedObject | null {
  if (typeof entry !== "object" || entry === null) {
    return null;
  }
  const e = entry as Record<string, unknown>;
  const rawType = e.type === "slider" ? "knob" : e.type;
  if (!ALLOWED_TYPES.includes(rawType as ObjectType)) {
    return null;
  }
  const type = rawType as ObjectType;
  const x = num(e.x);
  const y = num(e.y);
  const z = num(e.z);
  if (x === null || y === null || z === null) {
    return null;
  }
  const railPoints = parseRailPoints(e.railPoints);
  if (type === "rail" && railPoints.length < 2) {
    return null;
  }
  const d = defaultPlaced(type);
  const operator: ValueOperator = VALUE_OPERATORS.includes(e.operator as ValueOperator) ? (e.operator as ValueOperator) : d.operator;
  const pitchIndex = num(e.pitchIndex) ?? d.pitchIndex;
  const qx = num(e.qx);
  const qy = num(e.qy);
  const qz = num(e.qz);
  const qw = num(e.qw);
  const rotation = qx !== null && qy !== null && qz !== null && qw !== null
    ? new THREE.Quaternion(qx, qy, qz, qw)
    : new THREE.Quaternion();
  const params = new THREE.Vector4(
    num(e.px) ?? d.params.x,
    num(e.py) ?? d.params.y,
    num(e.pz) ?? d.params.z,
    num(e.pw) ?? d.params.w,
  );
  const scale = new THREE.Vector2(num(e.sx) ?? d.scale.x, num(e.sy) ?? d.scale.y);
  snapWidgetParamsToUnits(type, params);
  const resolvedTimbreId: Timbre = typeof e.timbreId === "string" && isTimbreId(e.timbreId) ? e.timbreId : d.timbreId;
  const timbreIds = parseTimbreIds(e.timbreIds, type, resolvedTimbreId);
  return {
    id: typeof e.id === "string" ? e.id : crypto.randomUUID(),
    type,
    position: new THREE.Vector3(x, y, z),
    rotation,
    restitution: type === "rail" ? 0 : (num(e.restitution) ?? d.restitution),
    friction: type === "rail" ? 0 : (num(e.friction) ?? d.friction),
    mass: num(e.mass) ?? d.mass,
    massEnabled: typeof e.massEnabled === "boolean" ? e.massEnabled : d.massEnabled,
    killOnContact: typeof e.killOnContact === "boolean" ? e.killOnContact : d.killOnContact,
    collisionEnabled: typeof e.collisionEnabled === "boolean" ? e.collisionEnabled : d.collisionEnabled,
    pitchIndex,
    timbreId: type === "timbre" ? (timbreIds[0] ?? resolvedTimbreId) : resolvedTimbreId,
    timbreIds,
    volume: type === "rail" ? (num(e.volume) ?? d.volume) : d.volume,
    muted: (() => {
      if (typeof e.muted === "boolean") {
        return e.muted;
      }
      if (type === "rail") {
        return false;
      }
      const raw = num(e.volume);
      return raw !== null ? raw <= 0 : e.audioEnabled === false;
    })(),
    fade: num(e.fade) ?? d.fade,
    operator,
    operand: num(e.operand) ?? num(e.value) ?? d.operand,
    decayS: num(e.decayS) ?? d.decayS,
    delayS: num(e.delayS) ?? d.delayS,
    routerHoldS: num(e.routerHoldS) ?? d.routerHoldS,
    routerHoldResetOnPulse: e.routerHoldResetOnPulse === true,
    timerMode: e.timerMode === "loop" ? "loop" : "time",
    knobValue: Math.min(Math.max(num(e.knobValue) ?? num(e.sliderValue) ?? d.knobValue, 0), 1),
    ...((): { knobMin: number; knobMax: number; knobInt: boolean } => {
      const knobInt = e.knobInt === true;
      const knobMin = num(e.knobMin) ?? d.knobMin;
      const knobMax = num(e.knobMax) ?? d.knobMax;
      return knobInt
        ? { knobInt, knobMin: Math.round(knobMin), knobMax: Math.round(knobMax) }
        : { knobInt, knobMin, knobMax };
    })(),
    buttonMode: e.buttonMode === "toggle" ? "toggle" : "push",
    buttonOn: e.buttonOn === true,
    buttonPulseOnReset: e.buttonPulseOnReset === true,
    lampHue: Math.min(Math.max(num(e.lampHue) ?? d.lampHue, 0), 1),
    lampSat: Math.min(Math.max(num(e.lampSat) ?? d.lampSat, 0), 1),
    lampValue: Math.min(Math.max(num(e.lampValue) ?? d.lampValue, 0), 1),
    ...((): { seqMin: number; seqMax: number; seqLen: number; seqValues: number[] } => {
      const seqMin = num(e.seqMin) ?? d.seqMin;
      const seqMax = num(e.seqMax) ?? d.seqMax;
      const seqLen = type === "curve" ? CURVE_DEFAULT_LEN : Math.max(SEQ_LEN_MIN, Math.round(num(e.seqLen) ?? d.seqLen));
      return { seqMin, seqMax, seqLen, seqValues: parseSeqValues(e.seqValues, type, seqLen, seqMin, seqMax) };
    })(),
    seqCycleLength: Math.max(1, Math.round(num(e.seqCycleLength) ?? d.seqCycleLength)),
    params,
    scale,
    arcDeg: num(e.arcDeg) ?? d.arcDeg,
    orbit: parseOrbitParams(e.orbit),
    railPoints,
    pitchIndexEnd: num(e.pitchIndexEnd) ?? pitchIndex,
    railTimbre: RAIL_TIMBRES.includes(e.railTimbre as RailTimbre) ? (e.railTimbre as RailTimbre) : d.railTimbre,
    name: typeof e.name === "string" ? e.name : "",
  };
}

/**
 * Maps a pitch index to a color so the scene reads as a score (DESIGN §5.2 ①).
 * The MIDI pitch class drives hue (one octave = one full wheel) and the
 * base-relative MIDI octave drives lightness (low octave = dark, high = bright).
 * Pure function — same pitch index always yields the same color.
 */
interface ColorSetHSL {
  saturation: number;
  lightnessMin: number;
  lightnessMax: number;
}

const COLOR_SET_HSL: Record<Exclude<ColorSet, "keyboard">, ColorSetHSL> = {
  pitch: { saturation: PITCH_SATURATION, lightnessMin: PITCH_LIGHTNESS_MIN, lightnessMax: PITCH_LIGHTNESS_MAX },
  mono: { saturation: 0, lightnessMin: 0.04, lightnessMax: 0.9 },
};

function pitchColor(pitchIndex: number, hsl: ColorSetHSL): THREE.Color {
  const midi = pitchIndexToMidi(pitchIndex);
  const pitchClass = ((midi % 12) + 12) % 12;
  const hue = pitchClass / 12;
  const octave = Math.floor(midi / 12) - Math.floor(AUDIO_BASE_MIDI / 12);
  const clampedOctave = Math.min(Math.max(octave, PITCH_OCTAVE_MIN), PITCH_OCTAVE_MAX);
  const t = (clampedOctave - PITCH_OCTAVE_MIN) / (PITCH_OCTAVE_MAX - PITCH_OCTAVE_MIN);
  const lightness = hsl.lightnessMin + (hsl.lightnessMax - hsl.lightnessMin) * t;
  return new THREE.Color().setHSL(hue, hsl.saturation, lightness);
}

const BLACK_KEY_CLASSES: ReadonlySet<number> = new Set([1, 3, 6, 8, 10]);

/**
 * Resolves a placed object's surface color from the active color set:
 * "keyboard" paints accidentals (piano black keys) near-black and naturals
 * near-white (not pure 0/1 so the standard material still shades); every
 * other set reuses the pitch-class hue / octave lightness mapping with its
 * own saturation and lightness range ("mono" at saturation 0 keeps only the
 * octave shading).
 */
function objectColor(pitchIndex: number, colorSet: ColorSet): THREE.Color {
  if (colorSet === "keyboard") {
    const midi = pitchIndexToMidi(pitchIndex);
    const pitchClass = ((midi % 12) + 12) % 12;
    return new THREE.Color(BLACK_KEY_CLASSES.has(pitchClass) ? KEY_COLOR_BLACK : KEY_COLOR_WHITE);
  }
  return pitchColor(pitchIndex, COLOR_SET_HSL[colorSet]);
}

interface ObjectMaterialParams {
  metalness: number;
  roughness: number;
  transparent: boolean;
  opacity: number;
  depthWrite: boolean;
}

/**
 * "glass" skips depth writes so particles inside a shell stay visible through
 * the translucent surface; the other presets differ only in metalness/roughness.
 */
const OBJECT_MATERIAL_PARAMS: Record<ObjectMaterial, ObjectMaterialParams> = {
  matte: { metalness: OBJECT_METALNESS, roughness: OBJECT_ROUGHNESS, transparent: false, opacity: 1, depthWrite: true },
  metal: { metalness: METAL_METALNESS, roughness: METAL_ROUGHNESS, transparent: false, opacity: 1, depthWrite: true },
  glossy: { metalness: 0, roughness: 0.08, transparent: false, opacity: 1, depthWrite: true },
  glass: { metalness: 0, roughness: 0.1, transparent: true, opacity: 0.45, depthWrite: false },
};

function materialParams(material: ObjectMaterial): ObjectMaterialParams {
  return OBJECT_MATERIAL_PARAMS[material];
}

function applyMaterialParams(material: THREE.MeshStandardMaterial, params: ObjectMaterialParams): void {
  material.metalness = params.metalness;
  material.roughness = params.roughness;
  material.transparent = params.transparent;
  material.opacity = params.opacity;
  material.depthWrite = params.depthWrite;
}

/**
 * Single source of truth for one placed object. `params` packs the size in a
 * type-specific layout that is shared verbatim by both the visual mesh and the
 * collision SDF uniform, so the two cannot drift:
 *   box    -> (halfX, halfY, halfZ, 0)
 *   sphere -> (radius, 0, 0, 0)
 *   funnel -> (rBottom, rTop, halfHeight, wallHalfThickness)
 *   force  -> (radius R, mode, strengthOrOmega, k)   mode 0=Lorentz / 1=Drag;
 *             Lorentz uses params.z as strength (k unused), Drag uses params.z
 *             as omega and params.w as k. CW/CCW is the sign of strength/omega.
 *   rail   -> unused; the shape lives in `railPoints` (control points in the
 *             local XY plane, relative to `position`), and the groove width is
 *             derived from the particle radius, not stored. `pitchIndex` /
 *             `pitchIndexEnd` are the rail's start / end pitch (the sustained
 *             voice glides between them); pitchIndexEnd is unused elsewhere.
 *
 * `massEnabled` gates gravity without destroying
 * the underlying mass value. `killOnContact` makes the object swallow
 * (kill) a particle on contact instead of bouncing it. `collisionEnabled` gates
 * the SDF collision entirely, so an object can attract (mass) without bouncing.
 * `fade` is the force field's soft-edge fraction (0 = hard cutoff at R, 1 = ramps
 * from the center): the force scales smoothly to zero over the outer `fade*R`.
 * `arcDeg` is the funnel's circumferential arc in degrees — the lathe sector
 * kept around +X (`[-arc/2, +arc/2]` of atan(z, x); 360 = the full revolution
 * shell). Every other type carries 360 and ignores it.
 * `timbreId` selects the synthesis voice played on contact (a catalog id); it is
 * orthogonal to `pitchIndex` (changing timbre never changes the stored value).
 * `timbreIds` is the set of timbres a `timbre` node governs (its volume /
 * transpose / wired envelope / wired pitch-quantize apply identically to each);
 * `timbreId` mirrors `timbreIds[0]` as the single-value representative the mesh
 * label and legacy code read. Other types ignore `timbreIds`.
 * `pitchIndex` is the contact pitch as a semitone offset from AUDIO_BASE_MIDI;
 * a `drum` timbre reads the same field modulo the kit size to pick which piece
 * it strikes (no separate pitch), so a value driving pitch keeps driving the
 * piece when the timbre flips to drum. `volume` (0..1) scales
 * the contact-voice gain per object on top of the contact dynamics and the
 * master volume; at 0 the object is mute and skips the contact-impulse scale
 * pulse. `operator` / `operand`
 * belong to the value node: `operator` is the operation it performs and `operand`
 * is the typed right-hand value used when its operand2In port is unconnected (the
 * live count of a `count` node lives runtime-only in the evaluator). `add` /
 * operand 0 on every other type. `decayS` belongs to the decay node: the seconds
 * its level takes to fall linearly from 1 to 0 (the level itself is runtime-only
 * in the evaluator); the default on every other type. `timerMode` belongs to the
 * timer node and selects what its valueOut emits; `time` on every other type.
 * `knobValue` belongs to the knob widget: its rotary position in [0,1], the
 * value its valueOut emits; the default on every other type. `buttonMode` /
 * `buttonOn` / `buttonPulseOnReset` belong to the button widget: the press
 * behavior, the latched toggle state (persisted so a switched-on scene reloads
 * on; meaningless in `push` mode, where the held state is runtime-only), and
 * whether a clock/transport reset auto-emits one pulse. `lampHue` / `lampSat`
 * belong to the lamp widget: the H and S of its light color in [0,1] (HSV with
 * V pinned to 1 — perceived brightness is a separate level, not a color
 * component); defaults on every other type. `lampValue` is the lamp's resting
 * brightness in [0,1] (persisted): the panel slider edits it, a Param-in wire
 * overrides it non-destructively, and a disconnect restores it. 0 on every
 * other type.
 */
export interface PlacedObject {
  id: string;
  type: ObjectType;
  position: THREE.Vector3;
  rotation: THREE.Quaternion;
  restitution: number;
  friction: number;
  mass: number;
  massEnabled: boolean;
  killOnContact: boolean;
  collisionEnabled: boolean;
  pitchIndex: number;
  timbreId: Timbre;
  timbreIds: Timbre[];
  volume: number;
  muted: boolean;
  fade: number;
  operator: ValueOperator;
  operand: number;
  decayS: number;
  delayS: number;
  routerHoldS: number;
  routerHoldResetOnPulse: boolean;
  timerMode: TimerMode;
  knobValue: number;
  knobMin: number;
  knobMax: number;
  knobInt: boolean;
  buttonMode: ButtonMode;
  buttonOn: boolean;
  buttonPulseOnReset: boolean;
  lampHue: number;
  lampSat: number;
  lampValue: number;
  seqMin: number;
  seqMax: number;
  seqLen: number;
  seqCycleLength: number;
  seqValues: number[];
  params: THREE.Vector4;
  scale: THREE.Vector2;
  arcDeg: number;
  orbit: OrbitParams;
  railPoints: THREE.Vector2[];
  pitchIndexEnd: number;
  railTimbre: RailTimbre;
  name: string;
}

/**
 * The value a knob's valueOut emits: its rotary position `knobValue` in [0,1]
 * mapped onto [knobMin, knobMax], rounded to an integer when `knobInt` is set.
 * Consumers of the knob's signal read this mapped value, not the raw rotary
 * position (the rotary cap angle and the panel position editor stay in [0,1]).
 */
export function knobOutput(o: PlacedObject): number {
  const mapped = o.knobMin + (o.knobMax - o.knobMin) * o.knobValue;
  return o.knobInt ? Math.round(mapped) : mapped;
}

/**
 * Snaps a knob's rotary position in [0,1] to the discrete positions whose mapped
 * output lands exactly on an integer — one position per integer in
 * [knobMin, knobMax], so an int knob's cap clicks between integer outputs rather
 * than sweeping continuously. Returns the position unchanged when the range spans
 * no integer step.
 */
export function snapKnobValue(knobValue: number, knobMin: number, knobMax: number): number {
  const steps = knobMax - knobMin;
  if (steps <= 0) {
    return knobValue;
  }
  return Math.round(knobValue * steps) / steps;
}

export interface CollisionUniforms {
  uObjCount: { value: number };
  uAlwaysCount: { value: number };
  uUseGrid: { value: number };
  uObjTex: { value: THREE.DataTexture };
  uRailTex: { value: THREE.DataTexture };
  uGridHeadTex: { value: THREE.DataTexture };
  uGridIdxTex: { value: THREE.DataTexture };
  uGridOrigin: { value: THREE.Vector2 };
  uCellSize: { value: number };
  uGridW: { value: number };
  uGridH: { value: number };
}

const GRID_IDX_TEX_H = Math.ceil((MAX_OBJECTS * (GRID_LARGE_SPAN_CELLS + 1) * (GRID_LARGE_SPAN_CELLS + 1)) / GRID_IDX_TEX_W);
const GRID_IDX_CAP = GRID_IDX_TEX_W * GRID_IDX_TEX_H;

/**
 * uObjTex packs the six per-object collider vec4 slots (A..F) as a 6-wide row
 * per collider: row k = collider slot k, column 0..5 = A..F. The shader reads
 * each slot with texelFetch(uObjTex, ivec2(col, k)), replacing six
 * uniform vec4[MAX_OBJECTS] arrays — those overflowed MAX_FRAGMENT_UNIFORM_VECTORS
 * (256) on low-end Adreno GPUs, so the link failed and gpu.compute() no-oped.
 * Column layout matches the former uObjA..F packing in pack() bit-for-bit.
 *
 * uRailTex carries each rail's sampled center line, one packed-slot row per
 * object (row k = collider slot k, so the shader needs no extra indirection):
 * RAIL_SAMPLES texels of (x, y, 0, 0) in object-local space. Rails do not fit
 * the per-object vec4 slots, so the segment list rides its own texture.
 *
 * uGridHeadTex / uGridIdxTex are the spatial-grid CSR for passive small
 * colliders (pack() bins them every frame). uGridHeadTex is GRID_MAX_DIM² and
 * holds (cellStart, cellCount) per cell in RG; uGridIdxTex is the flat list of
 * grid-object collision slot indices (R channel), addressed linearly. Force /
 * mass / large colliders skip the grid and ride the always-test prefix
 * [0, uAlwaysCount) of uObjTex instead, so the grid only carries cheap passive
 * colliders.
 */
export function createCollisionUniforms(): CollisionUniforms {
  const objTex = new THREE.DataTexture(
    new Float32Array(6 * MAX_OBJECTS * 4),
    6,
    MAX_OBJECTS,
    THREE.RGBAFormat,
    THREE.FloatType,
  );
  objTex.minFilter = THREE.NearestFilter;
  objTex.magFilter = THREE.NearestFilter;
  objTex.needsUpdate = true;
  const railTex = new THREE.DataTexture(
    new Float32Array(RAIL_SAMPLES * MAX_OBJECTS * 4),
    RAIL_SAMPLES,
    MAX_OBJECTS,
    THREE.RGBAFormat,
    THREE.FloatType,
  );
  railTex.minFilter = THREE.NearestFilter;
  railTex.magFilter = THREE.NearestFilter;
  railTex.needsUpdate = true;
  const gridHeadTex = new THREE.DataTexture(
    new Float32Array(GRID_MAX_DIM * GRID_MAX_DIM * 4),
    GRID_MAX_DIM,
    GRID_MAX_DIM,
    THREE.RGBAFormat,
    THREE.FloatType,
  );
  gridHeadTex.minFilter = THREE.NearestFilter;
  gridHeadTex.magFilter = THREE.NearestFilter;
  gridHeadTex.needsUpdate = true;
  const gridIdxTex = new THREE.DataTexture(
    new Float32Array(GRID_IDX_CAP * 4),
    GRID_IDX_TEX_W,
    GRID_IDX_TEX_H,
    THREE.RGBAFormat,
    THREE.FloatType,
  );
  gridIdxTex.minFilter = THREE.NearestFilter;
  gridIdxTex.magFilter = THREE.NearestFilter;
  gridIdxTex.needsUpdate = true;
  return {
    uObjCount: { value: 0 },
    uAlwaysCount: { value: 0 },
    uUseGrid: { value: 1 },
    uObjTex: { value: objTex },
    uRailTex: { value: railTex },
    uGridHeadTex: { value: gridHeadTex },
    uGridIdxTex: { value: gridIdxTex },
    uGridOrigin: { value: new THREE.Vector2() },
    uCellSize: { value: GRID_CELL_SIZE },
    uGridW: { value: 1 },
    uGridH: { value: 1 },
  };
}

export interface ObjectTemplate {
  id?: string;
  type: ObjectType;
  restitution: number;
  friction: number;
  mass: number;
  massEnabled: boolean;
  killOnContact: boolean;
  collisionEnabled: boolean;
  rotation: THREE.Quaternion;
  pitchIndex: number;
  timbreId: Timbre;
  timbreIds: Timbre[];
  volume: number;
  muted: boolean;
  fade: number;
  operator: ValueOperator;
  operand: number;
  decayS: number;
  delayS: number;
  routerHoldS: number;
  routerHoldResetOnPulse: boolean;
  timerMode: TimerMode;
  knobValue: number;
  knobMin: number;
  knobMax: number;
  knobInt: boolean;
  buttonMode: ButtonMode;
  buttonPulseOnReset: boolean;
  lampHue: number;
  lampSat: number;
  lampValue: number;
  seqMin: number;
  seqMax: number;
  seqLen: number;
  seqCycleLength: number;
  seqValues: number[];
  params: THREE.Vector4;
  scale: THREE.Vector2;
  arcDeg: number;
  orbit: OrbitParams;
  railPoints: THREE.Vector2[];
  pitchIndexEnd: number;
  railTimbre: RailTimbre;
  name?: string;
}

/**
 * Per-type initial `params` Vector4. A timbre node packs its three controls into
 * the slots — x = volume (0..1), y = sustain (0..1), z = transpose (semitones) —
 * so they ride the standard placed-object persistence with no extra fields. An
 * envelope node packs A/D/S/R as four normalized [0,1] knob values in x/y/z/w
 * (all four slots used); the default approximates the recipe AD so wiring it does
 * not change the timbre's voicing much.
 */
export function defaultParams(type: ObjectType): THREE.Vector4 {
  if (type === "box") {
    return new THREE.Vector4(DEFAULT_BOX_HALF.x, DEFAULT_BOX_HALF.y, DEFAULT_BOX_HALF.z, 0);
  }
  if (type === "sphere") {
    return new THREE.Vector4(DEFAULT_SPHERE_RADIUS, 0, 0, 0);
  }
  if (type === "force") {
    return new THREE.Vector4(DEFAULT_FIELD_RADIUS, DEFAULT_FIELD_MODE, DEFAULT_FIELD_OMEGA, DEFAULT_FIELD_K);
  }
  if (type === "button" || type === "lamp") {
    return new THREE.Vector4(WIDGET_DEFAULT_SCALE, 0, 0, 0);
  }
  if (type === "knob") {
    return new THREE.Vector4(WIDGET_DEFAULT_SCALE, 0, 0, 0);
  }
  if (type === "envelope") {
    return new THREE.Vector4(0.01, 0.1, 0, 0.08);
  }
  if (type === "array" || type === "curve") {
    return new THREE.Vector4(1, 1, 0, 0);
  }
  if (type === "display") {
    return new THREE.Vector4(DISPLAY_DEFAULT_W_UNITS, DISPLAY_DEFAULT_H_UNITS, 0, 0);
  }
  if (type === "timbre") {
    return new THREE.Vector4(1, 1, 0, 0);
  }
  if (type === "pitchQuantize") {
    return new THREE.Vector4(PENTATONIC_SCALE_MASK, 0, 0, 0);
  }
  if (type === "rail" || type === "calc" || type === "count" || type === "timer" || type === "decay" || type === "orbit" || type === "group" || type === "delay" || type === "router" || type === "global") {
    return new THREE.Vector4();
  }
  return new THREE.Vector4(
    DEFAULT_FUNNEL.rBottom,
    DEFAULT_FUNNEL.rTop,
    DEFAULT_FUNNEL.halfHeight,
    DEFAULT_FUNNEL.thickness,
  );
}

/**
 * Builds a fresh group node at a world position. A group is a non-collider rigid
 * transform parent: its collider fields carry defaults (never read) and its pose is
 * the parent frame its members ride. The marquee tool creates one per selection.
 */
export function makeGroup(position: THREE.Vector3): PlacedObject {
  return {
    id: crypto.randomUUID(),
    type: "group",
    position: position.clone(),
    rotation: new THREE.Quaternion(),
    restitution: DEFAULT_RESTITUTION,
    friction: DEFAULT_FRICTION,
    mass: DEFAULT_MASS,
    massEnabled: false,
    killOnContact: false,
    collisionEnabled: false,
    pitchIndex: 0,
    timbreId: "tone",
    timbreIds: ["tone"],
    volume: 1,
    muted: false,
    fade: DEFAULT_FIELD_FADE,
    operator: "add",
    operand: 0,
    decayS: DECAY_DEFAULT_S,
    delayS: DELAY_DEFAULT_S,
    routerHoldS: ROUTER_HOLD_DEFAULT_S,
    routerHoldResetOnPulse: false,
    timerMode: "loop",
    knobValue: KNOB_DEFAULT_VALUE,
    knobMin: KNOB_MIN_DEFAULT,
    knobMax: KNOB_MAX_DEFAULT,
    knobInt: false,
    buttonMode: "push",
    buttonOn: false,
    buttonPulseOnReset: false,
    lampHue: LAMP_DEFAULT_HUE,
    lampSat: LAMP_DEFAULT_SAT,
    lampValue: 0,
    ...defaultSeq("group"),
    seqCycleLength: CURVE_DEFAULT_CYCLE,
    params: new THREE.Vector4(),
    scale: new THREE.Vector2(1, 1),
    arcDeg: 360,
    orbit: defaultOrbitParams(),
    railPoints: [],
    pitchIndexEnd: 0,
    railTimbre: "sine",
    name: "",
  };
}

function placedFromTemplate(template: ObjectTemplate, position: THREE.Vector3): PlacedObject {
  return {
    id: crypto.randomUUID(),
    type: template.type,
    position: position.clone(),
    restitution: template.restitution,
    friction: template.friction,
    mass: template.mass,
    massEnabled: template.massEnabled,
    killOnContact: template.killOnContact,
    collisionEnabled: template.collisionEnabled,
    rotation: template.rotation.clone(),
    pitchIndex: template.pitchIndex,
    timbreId: template.timbreId,
    timbreIds: [...template.timbreIds],
    volume: template.volume,
    muted: template.muted,
    fade: template.fade,
    operator: template.operator,
    operand: template.operand,
    decayS: template.decayS,
    delayS: template.delayS,
    routerHoldS: template.routerHoldS,
    routerHoldResetOnPulse: template.routerHoldResetOnPulse,
    timerMode: template.timerMode,
    knobValue: template.knobValue,
    knobMin: template.knobMin,
    knobMax: template.knobMax,
    knobInt: template.knobInt,
    buttonMode: template.buttonMode,
    buttonOn: false,
    buttonPulseOnReset: template.buttonPulseOnReset,
    lampHue: template.lampHue,
    lampSat: template.lampSat,
    lampValue: template.lampValue,
    seqMin: template.seqMin,
    seqMax: template.seqMax,
    seqLen: template.seqLen,
    seqCycleLength: template.seqCycleLength,
    seqValues: Array.from({ length: template.seqLen }, (_v: unknown, i: number): number =>
      coerceSeqValue(template.type, template.seqValues[i] ?? template.seqMin, template.seqMin, template.seqMax)),
    params: template.params.clone(),
    scale: template.scale.clone(),
    arcDeg: template.arcDeg,
    orbit: cloneOrbitParams(template.orbit),
    railPoints: template.railPoints.map((p: THREE.Vector2): THREE.Vector2 => p.clone()),
    pitchIndexEnd: template.pitchIndexEnd,
    railTimbre: template.railTimbre,
    name: "",
  };
}

const FUNNEL_RIM_SEGMENTS = 4;

/**
 * Replaces each vertex of a convex closed polygon with a tangent arc of the
 * given radius, a decorative rounding of the funnel's rim corners. The
 * per-corner offset is clamped to half the shorter adjacent edge so thin
 * funnels degrade gracefully instead of self-intersecting.
 */
function filletPolygon(corners: THREE.Vector2[], radius: number, segments: number): THREE.Vector2[] {
  const n = corners.length;
  const out: THREE.Vector2[] = [];
  for (let i = 0; i < n; i++) {
    const v = corners[i];
    const prev = corners[(i - 1 + n) % n];
    const next = corners[(i + 1) % n];
    const u1 = prev.clone().sub(v).normalize();
    const u2 = next.clone().sub(v).normalize();
    const bis = u1.clone().add(u2);
    if (bis.lengthSq() < 1e-9) {
      out.push(v.clone());
      continue;
    }
    bis.normalize();
    const cosPhi = Math.min(Math.max(u1.dot(bis), -1), 1);
    const sinPhi = Math.sqrt(Math.max(1 - cosPhi * cosPhi, 0));
    if (sinPhi < 1e-4) {
      out.push(v.clone());
      continue;
    }
    const tanPhi = sinPhi / cosPhi;
    const maxOffset = 0.5 * Math.min(prev.distanceTo(v), next.distanceTo(v));
    const offset = Math.min(radius / tanPhi, maxOffset);
    const effR = offset * tanPhi;
    const t1 = v.clone().addScaledVector(u1, offset);
    const t2 = v.clone().addScaledVector(u2, offset);
    const center = v.clone().addScaledVector(bis, effR / sinPhi);
    const a1 = Math.atan2(t1.y - center.y, t1.x - center.x);
    const a2 = Math.atan2(t2.y - center.y, t2.x - center.x);
    let da = a2 - a1;
    while (da > Math.PI) da -= 2 * Math.PI;
    while (da < -Math.PI) da += 2 * Math.PI;
    for (let s = 0; s <= segments; s++) {
      const a = a1 + (da * s) / segments;
      out.push(new THREE.Vector2(center.x + effR * Math.cos(a), center.y + effR * Math.sin(a)));
    }
  }
  out.push(out[0].clone());
  return out;
}

/**
 * Closed lathe cross-section for the funnel wall in (radius, height) space,
 * revolved around the Y axis. The SDF (`sdFunnel`) shells the cone center line
 * by perpendicular half-thickness `t` and cuts the solid flat at y=±h; the
 * mesh draws that solid exactly (params = visible size, the same convention
 * box/sphere meshes use), so the visible flat walls sit at the center line
 * offset by `t` and the end caps at y=±h, horizontal and mutually parallel.
 * The four rim corners are filleted by COLLISION_RADIUS as decoration.
 */
function funnelProfile(params: THREE.Vector4): THREE.Vector2[] {
  const rB = params.x;
  const rT = params.y;
  const h = params.z;
  const t = params.w;
  const ex = rT - rB;
  const len = Math.hypot(ex, 2 * h);
  const ox = (t * 2 * h) / len;
  const oy = (t * -ex) / len;
  const slope = ex / (2 * h);
  const rOuter = (y: number): number => rB + ox + slope * (y + h - oy);
  const rInner = (y: number): number => rB - ox + slope * (y + h + oy);
  const outerBottom = new THREE.Vector2(rOuter(-h), -h);
  const outerTop = new THREE.Vector2(rOuter(h), h);
  const innerTop = new THREE.Vector2(rInner(h), h);
  const innerBottom = new THREE.Vector2(rInner(-h), -h);
  return filletPolygon([outerBottom, outerTop, innerTop, innerBottom], COLLISION_RADIUS, FUNNEL_RIM_SEGMENTS);
}

/**
 * Lathe phi relates to the SDF azimuth θ = atan(z, x) by phi = π/2 − θ, so the
 * phiStart/phiLength below place the funnel's arcDeg sector symmetric about +X —
 * exactly the sector sdFunnel keeps solid. At 360° the lathe is the same closed
 * shell as before (a phiStart shift only moves the seam).
 */
function shellGeometry(obj: PlacedObject): THREE.BufferGeometry {
  if (obj.type === "box") {
    return new RoundedBoxGeometry(
      obj.params.x * 2,
      obj.params.y * 2,
      obj.params.z * 2,
      2,
      COLLISION_RADIUS,
    );
  }
  if (obj.type === "sphere") {
    return new THREE.SphereGeometry(obj.params.x, 32, 24);
  }
  const arc = THREE.MathUtils.degToRad(obj.arcDeg);
  return new THREE.LatheGeometry(funnelProfile(obj.params), 48, Math.PI / 2 - arc / 2, arc);
}

/**
 * Caps for the two cut faces of a partial-arc funnel: one filleted cross-section
 * polygon per face. A profile built in the XY plane lands on the azimuth-θ
 * half-plane via rotateY(−θ) (the phi = π/2 − θ lathe mapping). The caller draws
 * them DoubleSide because ShapeGeometry's front face points outward on one cut
 * and inward on the other.
 */
function funnelArcCapGeometry(params: THREE.Vector4, arcHalf: number): THREE.BufferGeometry {
  const shape = new THREE.Shape(funnelProfile(params).slice(0, -1));
  const plus = new THREE.ShapeGeometry(shape);
  plus.rotateY(-arcHalf);
  const minus = new THREE.ShapeGeometry(shape);
  minus.rotateY(arcHalf);
  return mergeGeometries([plus, minus]);
}

/**
 * Flat fill for the funnel's z=0 cut opening, lying in the XY plane: the wall
 * cross-section on +x plus its mirror on -x, leaving the central opening empty.
 * Built DoubleSide because the mirrored half and the lathe-derived profile do
 * not carry a guaranteed front winding, so a single-sided cap would cull one
 * band. Sphere/box do not use a cap: they draw their full rounded body in
 * place, since a flat cap reads worse. A partial arc keeps only the +x band:
 * the -x band sits at azimuth 180°, which any arcDeg < 360 sector excludes,
 * while the +x band (azimuth 0, the sector center) always stays solid.
 */
function funnelCapGeometry(params: THREE.Vector4, arcDeg: number): THREE.BufferGeometry {
  const profile = funnelProfile(params).slice(0, -1);
  const right = new THREE.Shape(profile);
  if (arcDeg < 360) {
    return new THREE.ShapeGeometry(right);
  }
  const left = new THREE.Shape(profile.map((p: THREE.Vector2): THREE.Vector2 => new THREE.Vector2(-p.x, p.y)));
  return new THREE.ShapeGeometry([right, left]);
}

/**
 * Visible tube radius of one rail wall: a quarter of the particle size
 * (thin stainless wire look), floored so tiny particles keep a drawable rail.
 * The collision shader derives the same radius from uParticleRadius, and the
 * rail SDF is offset by tubeRadius so the contact surface sits exactly at the
 * visible tube surface plus the particle radius.
 */
export function railTubeRadius(particleRadius: number): number {
  return Math.max(particleRadius * RAIL_TUBE_FACTOR, RAIL_TUBE_MIN);
}

/**
 * Half the z-separation of the rail's wall-tube pair, which straddles the
 * curve plane at ±halfGap, so the wall separation is RAIL_GAP_RATIO of the
 * particle diameter. Staying below the contact reach (tube radius + particle
 * radius) guarantees a particle on the plane always touches the pair — it
 * rides the V-cradle between the tubes and cannot slip through.
 */
export function railHalfGap(particleRadius: number): number {
  return RAIL_GAP_RATIO * particleRadius;
}

/**
 * Samples the rail's Catmull-Rom center line at RAIL_SEGMENTS+1 arc-length-
 * uniform points in the local XY plane. Uniform spacing makes the normalized
 * arc position of sample j exactly j/RAIL_SEGMENTS, so the collision shader
 * derives the contact position t from the segment index alone.
 */
export function sampleRailCenterline(points: readonly THREE.Vector2[]): THREE.Vector3[] {
  const curve = new THREE.CatmullRomCurve3(
    points.map((p: THREE.Vector2): THREE.Vector3 => new THREE.Vector3(p.x, p.y, 0)),
  );
  return curve.getSpacedPoints(RAIL_SEGMENTS);
}

/**
 * Determines the array index at which a duplicated control point should be
 * inserted. The release position (object-local space) is compared against the
 * source point's neighbors: the new point is inserted on the segment closest
 * to the release position. For endpoints the outer (extension) side is chosen
 * when the release lies in the direction away from the only neighbor.
 */
export function insertRailPointIndex(
  points: readonly THREE.Vector2[],
  sourceIdx: number,
  releaseLocal: THREE.Vector2,
): number {
  const source = points[sourceIdx];
  const prev = sourceIdx > 0 ? points[sourceIdx - 1] : null;
  const next = sourceIdx < points.length - 1 ? points[sourceIdx + 1] : null;
  if (!prev && !next) {
    return sourceIdx + 1;
  }
  if (!prev) {
    const toNext = next!.clone().sub(source);
    const toRelease = releaseLocal.clone().sub(source);
    return toRelease.dot(toNext) >= 0 ? sourceIdx + 1 : sourceIdx;
  }
  if (!next) {
    const toPrev = prev.clone().sub(source);
    const toRelease = releaseLocal.clone().sub(source);
    return toRelease.dot(toPrev) >= 0 ? sourceIdx : sourceIdx + 1;
  }
  const distPrev = releaseLocal.distanceTo(prev);
  const distNext = releaseLocal.distanceTo(next);
  return distPrev < distNext ? sourceIdx : sourceIdx + 1;
}

const RAIL_TUBE_SEGMENTS_PER_SPAN = 24;
const RAIL_TUBE_RADIAL = 8;

function railWallTube(points: THREE.Vector3[], tubularSegments: number, radius: number, startColor: THREE.Color, endColor: THREE.Color, objectMaterial: ObjectMaterial): THREE.Mesh {
  const curve = new THREE.CatmullRomCurve3(points);
  const geometry = new THREE.TubeGeometry(curve, tubularSegments, radius, RAIL_TUBE_RADIAL, false);
  const count = geometry.getAttribute("position").count;
  const colors = new Float32Array(count * 3);
  const ringSize = RAIL_TUBE_RADIAL + 1;
  const c = new THREE.Color();
  for (let i = 0; i < count; i++) {
    const t = Math.min(Math.floor(i / ringSize) / tubularSegments, 1);
    c.copy(startColor).lerp(endColor, t);
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({
      vertexColors: true,
      ...materialParams(objectMaterial),
    }),
  );
}

/**
 * Rail mesh: two wall tubes along the center line, straddling the curve plane
 * at z = ±halfGap so the 2D front view shows a single line while the 3D view
 * reads as a twin-rail cradle. Colored as a start→end pitch gradient so the
 * glissando direction reads at a glance. The root mesh is an invisible fat
 * tube along the center line (the raycast / halo target, the same idiom as
 * the orbit node's pick sphere) because the thin walls alone are hard to hit.
 */
function buildRailMesh(obj: PlacedObject, preview: boolean, particleRadius: number, colorSet: ColorSet, objectMaterial: ObjectMaterial): THREE.Mesh {
  const center = sampleRailCenterline(obj.railPoints);
  const halfGap = railHalfGap(particleRadius);
  const wallRadius = railTubeRadius(particleRadius);
  const tubularSegments = RAIL_TUBE_SEGMENTS_PER_SPAN * (obj.railPoints.length - 1);
  const pick = new THREE.Mesh(
    new THREE.TubeGeometry(new THREE.CatmullRomCurve3(center), tubularSegments, halfGap + wallRadius + 0.2, RAIL_TUBE_RADIAL, false),
    new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }),
  );
  const startColor = preview ? new THREE.Color(PREVIEW_COLOR) : objectColor(obj.pitchIndex, colorSet);
  const endColor = preview ? new THREE.Color(PREVIEW_COLOR) : objectColor(obj.pitchIndexEnd, colorSet);
  for (const side of [1, -1]) {
    const wall = railWallTube(center, tubularSegments, wallRadius, startColor, endColor, objectMaterial);
    wall.position.z = side * halfGap;
    wall.castShadow = !preview;
    if (preview) {
      const material = wall.material as THREE.MeshStandardMaterial;
      material.transparent = true;
      material.opacity = 0.5;
      material.depthWrite = false;
    }
    pick.add(wall);
  }
  pick.position.copy(obj.position);
  pick.quaternion.copy(obj.rotation);
  return pick;
}

/**
 * Builds the force-field gizmo: a single flat ring at the disk boundary (radius
 * R) in the z=0 plane. The interior is left empty so particles read clearly
 * through it; the ring band is the pickable target. Fixed color (pitch does not
 * apply to a force).
 */
function buildForceMesh(obj: PlacedObject, preview: boolean): THREE.Mesh {
  const R = obj.params.x;
  const color = preview ? new THREE.Color(PREVIEW_COLOR) : FORCE_COLOR.clone();
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(R * 0.94, R, 64),
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: preview ? 0.6 : 0.9,
      side: THREE.DoubleSide,
      depthWrite: false,
    }),
  );
  ring.position.copy(obj.position);
  ring.quaternion.copy(obj.rotation);
  return ring;
}

const NODE_ICON_HALF = 0.4;
const NODE_ICON_BORDER_FRAC = 0.1;
const VALUE_COLOR = new THREE.Color(0x6cf0a0);
const TIMBRE_COLOR = new THREE.Color(0xb08cf0);
const PITCH_KEY_ON_COLOR = new THREE.Color(0x35e06a);
const PITCH_KEY_BLACK_ON_COLOR = new THREE.Color(0x239049);
const PITCH_KEY_WHITE_COLOR = new THREE.Color(0xdfe4ec);
const PITCH_KEY_BLACK_COLOR = new THREE.Color(0x23272f);
const TIMER_COLOR = new THREE.Color(0x6cd0f0);
const DECAY_COLOR = new THREE.Color(0xf0a86c);
const DELAY_COLOR = new THREE.Color(0xf06ca0);
const ROUTER_COLOR = new THREE.Color(0xc0d860);
const GLOBAL_COLOR = new THREE.Color(0x8c9cf0);
const SEQ_CELL_OFF_COLOR = new THREE.Color(0x2a3340);
const SEQ_CELL_ON_COLOR = new THREE.Color(0x60d0c0);
/** The current-position highlight: the off-cell background hue lifted brighter and more saturated. */
const SEQ_CURSOR_COLOR = ((): THREE.Color => {
  const hsl = { h: 0, s: 0, l: 0 };
  SEQ_CELL_OFF_COLOR.getHSL(hsl);
  return new THREE.Color().setHSL(hsl.h, Math.min(1, hsl.s + 0.35), Math.min(1, hsl.l + 0.2));
})();
const SEQ_BORDER_COLOR = new THREE.Color(0x10161e);
export const SEQ_FRAME_W = 0.26;
const SEQ_PLATE_DEPTH = 0.18;
const DISPLAY_SCREEN_COLOR = new THREE.Color(0x080a0c);
const DISPLAY_SEG_ON_COLOR = new THREE.Color(0x3dd23e);
const DISPLAY_SEG_OFF_COLOR = new THREE.Color(0x14171a);
/** The display panel's frame border, half the shared seq-widget frame, so the black screen (and its digits) fill more of the panel. */
const DISPLAY_FRAME_W = SEQ_FRAME_W / 2;
const DISPLAY_DIGIT_ASPECT = 0.62;
const DISPLAY_DIGIT_GAP = 1.06;
const DISPLAY_SIDE_PAD_FRAC = 0.22;
const CURVE_LINE_COLOR = new THREE.Color(0x9cd0ff);
const ENVELOPE_CURVE_COLOR = new THREE.Color(0x35e06a);
const ENVELOPE_GRID_COLOR = new THREE.Color(0x39414f);
const ORBIT_COLOR = new THREE.Color(0xffb060);
const ORBIT_RING_RADIUS = 0.55;

/**
 * Pickable gizmo for an orbit node: a thin ring around a small hub at the pivot,
 * reading as an orbital path. The visible ring/hub are children of an invisible
 * pick sphere (the main mesh) so a click anywhere inside the ring selects it —
 * the ring's own tube is too thin to hit reliably with a raycast, and the sphere
 * geometry's bounding box is what the selection frame wraps. Unlit
 * fixed color (pitch / scale do not apply); never enters the collision SDF — it
 * only drives connected objects.
 */
function buildOrbitMesh(obj: PlacedObject, preview: boolean): THREE.Mesh {
  const color = preview ? new THREE.Color(PREVIEW_COLOR) : ORBIT_COLOR.clone();
  const material = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: preview ? 0.5 : 0.9,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const pick = new THREE.Mesh(
    new THREE.SphereGeometry(ORBIT_RING_RADIUS, 16, 12),
    new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }),
  );
  const ring = new THREE.Mesh(new THREE.TorusGeometry(ORBIT_RING_RADIUS, 0.045, 8, 48), material);
  const hub = new THREE.Mesh(new THREE.SphereGeometry(0.12, 16, 12), material);
  pick.add(ring);
  pick.add(hub);
  pick.position.copy(obj.position);
  pick.quaternion.copy(obj.rotation);
  return pick;
}

/**
 * Pickable target for a group node: a single invisible unit box wrapping its members.
 * The per-frame group-box pass (`setGroupBox`) drives its world pose / scale to the
 * members' OBB, so a click anywhere inside the cluster selects the group; selection is
 * shown by the OBB selection frame parented to this box, so no separate outline is drawn.
 * Never enters the collision SDF.
 */
function buildGroupMesh(obj: PlacedObject): THREE.Mesh {
  const pick = new THREE.Mesh(
    new THREE.BoxGeometry(1, 1, 1),
    new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }),
  );
  pick.position.copy(obj.position);
  return pick;
}

const WIDGET_BASE_COLOR = new THREE.Color(0x3a4150);
const BUTTON_CAP_COLOR = new THREE.Color(0xe24040);
const BUTTON_CAP_PRESSED_COLOR = BUTTON_CAP_COLOR.clone().multiplyScalar(0.45);

/**
 * A lamp's light color from its hue / saturation, HSV with V pinned to 1
 * (brightness lives in the emissive intensity, not the color). Mapped through
 * setHSL via L = 1 - S/2 at full HSL saturation, which equals HSV(h, s, 1);
 * s = 0 degenerates to white for any hue.
 */
export function lampColor(hue: number, sat: number): THREE.Color {
  return new THREE.Color().setHSL(hue, sat <= 0 ? 0 : 1, 1 - sat / 2);
}
const BLOOM_LUMA_WEIGHTS: THREE.Vector3 = THREE.ColorManagement.getLuminanceCoefficients(
  new THREE.Vector3(),
);

/**
 * Linear-space emissive color for a lit lamp dome at full level. UnrealBloomPass
 * thresholds on rec709 luminance but blurs per-channel, so a single-channel
 * saturated color (blue) needs a huge channel value to reach the luminance
 * threshold and then blooms far brighter than balanced hues. To keep the bloom
 * magnitude comparable while still letting color read through:
 *  - the brightest channel is scaled to a peak that ramps from LAMP_PEAK_MIN at
 *    sat=0 to LAMP_PEAK_MAX at sat=1, so desaturated lamps keep a calm
 *    brightness and saturated lamps push their main channel for a colored-film
 *    look;
 *  - only the minimum white needed to clear BLOOM_THRESHOLD + LAMP_BLOOM_LUMA_MARGIN
 *    is mixed in, so low-luminance hues (blue/red/purple) still bloom but stay
 *    as chromatic as the threshold allows.
 * Returns a color for material.emissive; emissiveIntensity carries the [0,1] level.
 */
function lampEmissiveColor(hue: number, sat: number, out: THREE.Color): THREE.Color {
  out.setHSL(hue, sat <= 0 ? 0 : 1, 1 - sat / 2, THREE.SRGBColorSpace);
  const clampedSat = Math.max(0, Math.min(1, sat));
  const peak = LAMP_PEAK_MIN + (LAMP_PEAK_MAX - LAMP_PEAK_MIN) * clampedSat;
  const chan = Math.max(out.r, out.g, out.b, 1e-3);
  const k = peak / chan;
  out.r *= k;
  out.g *= k;
  out.b *= k;
  const w = BLOOM_LUMA_WEIGHTS;
  const luma = w.x * out.r + w.y * out.g + w.z * out.b;
  const lumaTarget = BLOOM_THRESHOLD + LAMP_BLOOM_LUMA_MARGIN;
  if (luma < lumaTarget) {
    const floor = lumaTarget - luma;
    out.r += floor;
    out.g += floor;
    out.b += floor;
  }
  return out;
}
export const KNOB_BASE_RADIUS = 0.4;
export const KNOB_BODY_RADIUS = 0.3;
const KNOB_BASE_HEIGHT = SEQ_PLATE_DEPTH;
const KNOB_BODY_HEIGHT = 0.26;
const KNOB_INDICATOR_HALF = new THREE.Vector3(0.045, 0.025, 0.04);
const KNOB_INDICATOR_COLOR = new THREE.Color(0xe23b3b);
export const KNOB_VALUE_LABEL_Z = 0.15;

export const BUTTON_BASE_RADIUS = KNOB_BASE_RADIUS;
const BUTTON_BASE_HEIGHT = KNOB_BASE_HEIGHT;
const BUTTON_CAP_RADIUS = 0.3;
const BUTTON_CAP_HEIGHT = 0.22;
export const BUTTON_CAP_PRESS_SINK = 0.08;
export const LAMP_BASE_RADIUS = KNOB_BASE_RADIUS;
const LAMP_BASE_HEIGHT = KNOB_BASE_HEIGHT;

/**
 * The widget size grid: one unit is the Button background box's side
 * (BUTTON_BASE_RADIUS × 2). Every widget's outer size is an integer multiple of
 * this so placed widgets line up on a shared lattice; resizes snap to it.
 * Derived from BUTTON_BASE_RADIUS so the two never drift apart.
 */
export const WIDGET_UNIT = BUTTON_BASE_RADIUS * 2;

/** Largest diameter, in grid units, a round widget (button / lamp / knob) resizes to; its scale is the unit count so 1U = scale 1. */
export const WIDGET_MAX_UNITS = 4;

export const DISPLAY_DEFAULT_W_UNITS = 3;
export const DISPLAY_DEFAULT_H_UNITS = 1;
export const DISPLAY_MIN_W_UNITS = 2;
export const DISPLAY_MIN_H_UNITS = 1;
export const DISPLAY_MAX_W_UNITS = 10;
export const DISPLAY_MAX_H_UNITS = 4;
const LAMP_DOME_RADIUS = 0.27;
const LAMP_DISC_HEIGHT = 0.05;
const LAMP_DOME_BACK_MARGIN = 0.06;
const LAMP_DOME_Z = -LAMP_BASE_HEIGHT / 2 + LAMP_DOME_BACK_MARGIN;
const LAMP_DISC_TOP_Z = LAMP_BASE_HEIGHT / 2 + LAMP_DISC_HEIGHT;
/** Radius of the circle where the dome's hemisphere surface pokes up through the disc's top plane — the part of the dome actually visible above it. */
const LAMP_DOME_VISIBLE_RADIUS = Math.sqrt(Math.max(0, LAMP_DOME_RADIUS ** 2 - (LAMP_DISC_TOP_Z - LAMP_DOME_Z) ** 2));
const LAMP_DISC_MARGIN = 0.08;
const LAMP_DISC_RADIUS = LAMP_DOME_VISIBLE_RADIUS + LAMP_DISC_MARGIN;
export const NODE_TITLE_LABEL_DY = 0.62;

/**
 * Node-type objects (graph / widget nodes, no particle-facing mass) that spawn
 * offset in front of a default box so stacking one on a box reads as "node in
 * front" instead of the node being buried inside the box's collider volume.
 */
const NODE_SPAWN_TYPES: ReadonlySet<ObjectType> = new Set([
  "calc", "count", "display", "timer", "decay", "orbit", "group", "timbre", "pitchQuantize",
  "delay", "router", "button", "lamp", "knob", "envelope", "array", "curve", "global",
]);

/**
 * Distance from a node's spawn position to its own back face, per type — the
 * geometry each `build*Mesh` function actually produces. Types built symmetric
 * about the object position (button/knob/lamp/envelope/pitchQuantize/the plain
 * node icon/group's pick box/orbit's gizmo) report their half-thickness; the
 * sequencer-plate types (count/array/curve) anchor at their own front face and
 * extend backward by their full plate depth.
 */
function nodeBackFaceDistance(type: ObjectType): number {
  switch (type) {
    case "button":
      return BUTTON_BASE_HEIGHT / 2;
    case "knob":
      return KNOB_BASE_HEIGHT / 2;
    case "lamp":
      return LAMP_BASE_HEIGHT / 2;
    case "display":
    case "array":
    case "curve":
      return SEQ_PLATE_DEPTH;
    case "envelope":
      return ENVELOPE_BASE_HALF.z;
    case "pitchQuantize":
      return PITCH_QUANTIZE_BASE_HALF.z;
    case "calc":
    case "count":
    case "timer":
    case "decay":
    case "timbre":
    case "delay":
    case "router":
    case "global":
      return KNOB_BASE_HEIGHT / 2;
    case "group":
      return 0.5;
    case "orbit":
      return ORBIT_RING_RADIUS;
    default:
      return 0;
  }
}

function nodeSpawnPosition(type: ObjectType, position: THREE.Vector3): THREE.Vector3 {
  return NODE_SPAWN_TYPES.has(type)
    ? new THREE.Vector3(position.x, position.y, position.z + DEFAULT_BOX_HALF.z + nodeBackFaceDistance(type))
    : position;
}

/**
 * Total angular sweep of a rotary knob: value 0..1 spans 300° about its local
 * +Z, swept symmetrically over the top so the minimum points to the lower-left,
 * the maximum to the lower-right, and a 60° dead zone sits straight down — the
 * hardware-knob convention. Increasing the value turns the indicator clockwise.
 */
export const KNOB_SWEEP_RAD = (300 * Math.PI) / 180;
const KNOB_UP_RAD = Math.PI / 2;

function widgetPartMaterial(color: THREE.Color, preview: boolean): THREE.MeshStandardMaterial {
  if (preview) {
    return new THREE.MeshStandardMaterial({
      color: PREVIEW_COLOR,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
    });
  }
  return new THREE.MeshStandardMaterial({ color, metalness: 0.1, roughness: 0.55 });
}

const WIDGET_EDGE_RADIUS = 0.06;
const WIDGET_EDGE_SEGMENTS = 4;

/**
 * Solid cylinder with filleted rim edges (the cylinder counterpart of the
 * RoundedBoxGeometry the box object uses), built as a lathe of a rounded
 * profile: axis → bottom face → quarter-arc → wall → quarter-arc → top face.
 * Axis along +z like every widget part.
 */
function widgetCylinderGeometry(radius: number, height: number): THREE.BufferGeometry {
  const h = height / 2;
  const r = Math.min(WIDGET_EDGE_RADIUS, radius / 2, h);
  const points: THREE.Vector2[] = [new THREE.Vector2(0, -h)];
  for (let i = 0; i <= WIDGET_EDGE_SEGMENTS; i++) {
    const a = -Math.PI / 2 + (i / WIDGET_EDGE_SEGMENTS) * (Math.PI / 2);
    points.push(new THREE.Vector2(radius - r + Math.cos(a) * r, -h + r + Math.sin(a) * r));
  }
  for (let i = 0; i <= WIDGET_EDGE_SEGMENTS; i++) {
    const a = (i / WIDGET_EDGE_SEGMENTS) * (Math.PI / 2);
    points.push(new THREE.Vector2(radius - r + Math.cos(a) * r, h - r + Math.sin(a) * r));
  }
  points.push(new THREE.Vector2(0, h));
  const geometry = new THREE.LatheGeometry(points, 32);
  geometry.rotateX(Math.PI / 2);
  return geometry;
}

/**
 * Button widget: a rounded square base (the selection / drag body, the root
 * mesh, matching the knob/lamp base) with a red cap cylinder resting on top,
 * marked `userData.widgetControl`. On press the cap simply moves down by
 * `BUTTON_CAP_PRESS_SINK`, so the pressed state reads at a glance. Rest height
 * sits `BUTTON_CAP_PRESS_SINK` below the cap-flush-on-base height, so the total
 * travel between rest and pressed stays a fixed `BUTTON_CAP_PRESS_SINK` low
 * profile independent of the base/cap dimensions. `userData.buttonCap` /
 * `userData.capRestZ` let setButtonPressed move the cap without a rebuild;
 * both parts cast and the base receives shadows. Fixed colors (pitch does not
 * apply); never enters the collision SDF.
 */
function buildButtonMesh(obj: PlacedObject, preview: boolean): THREE.Mesh {
  const side = BUTTON_BASE_RADIUS * 2;
  const base = new THREE.Mesh(
    new RoundedBoxGeometry(side, side, BUTTON_BASE_HEIGHT, 2, WIDGET_EDGE_RADIUS),
    widgetPartMaterial(WIDGET_BASE_COLOR, preview),
  );
  const cap = new THREE.Mesh(
    widgetCylinderGeometry(BUTTON_CAP_RADIUS, BUTTON_CAP_HEIGHT),
    widgetPartMaterial(BUTTON_CAP_COLOR, preview),
  );
  const restZ = BUTTON_BASE_HEIGHT / 2 + BUTTON_CAP_HEIGHT / 2 - BUTTON_CAP_PRESS_SINK;
  const latchedOn = obj.buttonMode === "toggle" && obj.buttonOn;
  cap.position.z = restZ - (latchedOn ? BUTTON_CAP_PRESS_SINK : 0);
  if (latchedOn && !preview) {
    (cap.material as THREE.MeshStandardMaterial).color.copy(BUTTON_CAP_PRESSED_COLOR);
  }
  cap.userData.widgetControl = true;
  cap.castShadow = !preview;
  base.add(cap);
  base.userData.buttonCap = cap;
  base.userData.capRestZ = restZ;
  base.castShadow = !preview;
  base.receiveShadow = !preview;
  base.position.copy(obj.position);
  base.quaternion.copy(obj.rotation);
  base.scale.setScalar(widgetScale(obj));
  return base;
}

/**
 * Lamp widget: a rounded square base (root mesh, matching the knob/button base)
 * with the lamp's original round disc layered on top as a raised detail, and an
 * emissive dome sunk down into the base/disc stack so the dome's own radius
 * does not dominate the widget's total height — its open (hollow) face is kept
 * `LAMP_DOME_BACK_MARGIN` above the base's own back face so it never pokes out
 * the back. The whole body is selection target — a lamp has no control part.
 * `userData.lampDome` lets the per-frame signal drive set the dome's
 * emissiveIntensity; the dome's diffuse is darkened so the off→on contrast
 * reads even without bloom. Fixed colors; never enters the collision SDF.
 */
function buildLampMesh(obj: PlacedObject, preview: boolean): THREE.Mesh {
  const color = lampColor(obj.lampHue, obj.lampSat);
  const side = LAMP_BASE_RADIUS * 2;
  const base = new THREE.Mesh(
    new RoundedBoxGeometry(side, side, LAMP_BASE_HEIGHT, 2, WIDGET_EDGE_RADIUS),
    widgetPartMaterial(WIDGET_BASE_COLOR, preview),
  );
  const disc = new THREE.Mesh(
    widgetCylinderGeometry(LAMP_DISC_RADIUS, LAMP_DISC_HEIGHT),
    widgetPartMaterial(WIDGET_BASE_COLOR, preview),
  );
  disc.position.z = LAMP_DISC_TOP_Z - LAMP_DISC_HEIGHT / 2;
  disc.castShadow = !preview;
  disc.receiveShadow = !preview;
  base.add(disc);
  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(LAMP_DOME_RADIUS, 24, 16, 0, Math.PI * 2, 0, Math.PI / 2),
    widgetPartMaterial(color, preview),
  );
  const domeMaterial = dome.material as THREE.MeshStandardMaterial;
  if (!preview) {
    domeMaterial.color.multiplyScalar(0.4);
  }
  lampEmissiveColor(obj.lampHue, obj.lampSat, domeMaterial.emissive);
  domeMaterial.emissiveIntensity = 0;
  dome.rotation.x = Math.PI / 2;
  dome.position.z = LAMP_DOME_Z;
  dome.castShadow = !preview;
  base.add(dome);
  base.userData.lampDome = dome;
  base.castShadow = !preview;
  base.receiveShadow = !preview;
  base.position.copy(obj.position);
  base.quaternion.copy(obj.rotation);
  base.scale.setScalar(widgetScale(obj));
  return base;
}

/** Orients a rotary control's indicator from a [0,1] value: up at 0.5, lower-left at 0, lower-right at 1. */
export function setKnobAngle(control: THREE.Object3D, value: number): void {
  control.rotation.z = KNOB_UP_RAD - (Math.min(Math.max(value, 0), 1) - 0.5) * KNOB_SWEEP_RAD;
}

/**
 * One reusable rotary knob control: a short cylinder cap with a radial indicator
 * notch on its top face, marked `userData.widgetControl` so the pointer routes a
 * hit to grab handling instead of selection, and `userData.knobParam` so the
 * grab knows which parameter it drives. The cap spins about local +Z; its
 * `rotation.z` encodes the value (see setKnobAngle). Used both as a standalone
 * knob object and as a subcontrol mounted on the envelope body.
 */
function buildKnobControl(paramName: string, value: number, preview: boolean): THREE.Mesh {
  const cap = new THREE.Mesh(
    widgetCylinderGeometry(KNOB_BODY_RADIUS, KNOB_BODY_HEIGHT),
    widgetPartMaterial(WIDGET_BASE_COLOR, preview),
  );
  const notch = new THREE.Mesh(
    new THREE.BoxGeometry(KNOB_INDICATOR_HALF.x * 2, KNOB_INDICATOR_HALF.y * 2, KNOB_INDICATOR_HALF.z * 2),
    widgetPartMaterial(KNOB_INDICATOR_COLOR, preview),
  );
  notch.position.set(KNOB_BODY_RADIUS, 0, KNOB_BODY_HEIGHT / 2);
  notch.castShadow = !preview;
  cap.add(notch);
  cap.userData.widgetControl = true;
  cap.userData.knobParam = paramName;
  cap.castShadow = !preview;
  cap.receiveShadow = !preview;
  setKnobAngle(cap, value);
  return cap;
}

/**
 * Knob widget: a low square base plate (the selection / drag body, the root mesh)
 * with a rotary cap subcontrol on +Z whose angle encodes `knobValue` in [0,1] —
 * the value its valueOut emits. `userData.knobControls` maps each param name to
 * its cap so setKnobValue can re-angle it without a rebuild. Fixed colors; never
 * enters the collision SDF.
 */
function buildKnobMesh(obj: PlacedObject, preview: boolean): THREE.Mesh {
  const side = KNOB_BASE_RADIUS * 2;
  const base = new THREE.Mesh(
    new RoundedBoxGeometry(side, side, KNOB_BASE_HEIGHT, 2, WIDGET_EDGE_RADIUS),
    widgetPartMaterial(WIDGET_BASE_COLOR, preview),
  );
  const cap = buildKnobControl("value", obj.knobValue, preview);
  cap.position.z = KNOB_BASE_HEIGHT / 2;
  base.add(cap);
  base.userData.knobControls = { value: cap };
  base.castShadow = !preview;
  base.receiveShadow = !preview;
  base.position.copy(obj.position);
  base.quaternion.copy(obj.rotation);
  base.scale.setScalar(widgetScale(obj));
  return base;
}

export const ENVELOPE_PARAM_ORDER: readonly string[] = ["attack", "decay", "sustain", "release"];
const ENVELOPE_KNOB_SPACING = 0.74;
export const ENVELOPE_BASE_HALF = new THREE.Vector3(2 * WIDGET_UNIT, 1.5 * WIDGET_UNIT, 0.09);
const ENVELOPE_KNOB_ROW_Y = -0.54;
export const ENVELOPE_KNOB_LABEL_DY = -0.47;
const ENVELOPE_CURVE_HALF_W = 1.35;
const ENVELOPE_CURVE_Y0 = -0.05;
const ENVELOPE_CURVE_Y1 = 1.0;
const ENVELOPE_CURVE_PANEL_PAD = 0.12;
const ENVELOPE_OVERLAY_Z = ENVELOPE_BASE_HALF.z + 0.012;
const ENVELOPE_PANEL_Z = ENVELOPE_OVERLAY_Z;
const ENVELOPE_GRID_Z = ENVELOPE_OVERLAY_Z;
const ENVELOPE_CURVE_Z = ENVELOPE_OVERLAY_Z;
const ENVELOPE_GRID_MAX_LINES = Math.floor(ENVELOPE_ATTACK_MAX_S + ENVELOPE_DECAY_MAX_S + ENVELOPE_RELEASE_MAX_S) + 1;

/** Local-space center of each envelope knob cap, in palette order (attack, decay, sustain, release). */
export function envelopeKnobLayout(): readonly { param: string; x: number; y: number }[] {
  return ENVELOPE_PARAM_ORDER.map((param: string, i: number): { param: string; x: number; y: number } => ({
    param,
    x: (i - 1.5) * ENVELOPE_KNOB_SPACING,
    y: ENVELOPE_KNOB_ROW_Y,
  }));
}

/**
 * Local-space polyline (4 points, flat xyz) of the impulse ADSR shape for the
 * envelope's graph panel: 0→1 over attack, →sustain level over decay, →0 over
 * release. There is no sustain-hold segment (a one-shot voice has zero hold), so
 * the curve's full width is the real attack+decay+release time — segment widths
 * are proportional to each segment's seconds (knob value × its max), mapped into
 * the plate's upper rectangle. Decay/release are floored so the curve never
 * collapses, but attack is not: at attack=0 the leading rise is a true vertical.
 */
function envelopeCurvePositions(p: THREE.Vector4): Float32Array {
  const a = Math.max(p.x * ENVELOPE_ATTACK_MAX_S, 0);
  const d = Math.max(p.y * ENVELOPE_DECAY_MAX_S, 0.01);
  const r = Math.max(p.w * ENVELOPE_RELEASE_MAX_S, 0.01);
  const s = Math.min(Math.max(p.z, 0), 1);
  const total = a + d + r;
  const nx = [0, a, a + d, total];
  const ny = [0, 1, s, 0];
  const out = new Float32Array(12);
  for (let i = 0; i < 4; i++) {
    out[i * 3] = -ENVELOPE_CURVE_HALF_W + (nx[i] / total) * (2 * ENVELOPE_CURVE_HALF_W);
    out[i * 3 + 1] = ENVELOPE_CURVE_Y0 + ny[i] * (ENVELOPE_CURVE_Y1 - ENVELOPE_CURVE_Y0);
    out[i * 3 + 2] = ENVELOPE_CURVE_Z;
  }
  return out;
}

/**
 * Fills `out` with a vertical line at each integer second from 0 to floor(total),
 * where total is the same attack+decay+release time the curve spans, so the grid
 * reads as a per-second time scale beneath the ADSR shape. Each line is two
 * vertices (panel bottom → top). Returns the line count so the caller sets the
 * LineSegments draw range; `out` must hold ENVELOPE_GRID_MAX_LINES lines.
 */
function envelopeGridPositions(p: THREE.Vector4, out: Float32Array): number {
  const a = Math.max(p.x * ENVELOPE_ATTACK_MAX_S, 0);
  const d = Math.max(p.y * ENVELOPE_DECAY_MAX_S, 0.01);
  const r = Math.max(p.w * ENVELOPE_RELEASE_MAX_S, 0.01);
  const total = a + d + r;
  const lineCount = Math.min(ENVELOPE_GRID_MAX_LINES, Math.floor(total) + 1);
  for (let k = 0; k < lineCount; k++) {
    const x = -ENVELOPE_CURVE_HALF_W + (k / total) * (2 * ENVELOPE_CURVE_HALF_W);
    out[k * 6] = x;
    out[k * 6 + 1] = ENVELOPE_CURVE_Y0;
    out[k * 6 + 2] = ENVELOPE_GRID_Z;
    out[k * 6 + 3] = x;
    out[k * 6 + 4] = ENVELOPE_CURVE_Y1;
    out[k * 6 + 5] = ENVELOPE_GRID_Z;
  }
  return lineCount;
}

/**
 * Envelope widget: a tall base plate (the selection / drag body, the root mesh)
 * carrying four rotary knob subcontrols in a row along the bottom — attack /
 * decay / sustain / release, reading params x / y / z / w — and an ADSR graph
 * line across the upper area that redraws from those params (userData.envelopeCurve).
 * Each cap is a reusable knob control tagged with its `knobParam`, and
 * `userData.knobControls` maps the param name to its cap so setKnobIndicator
 * re-angles one without a rebuild. The plate shares the knob cap color. Non-collider;
 * never enters the collision SDF.
 */
function buildEnvelopeMesh(obj: PlacedObject, preview: boolean): THREE.Mesh {
  const base = new THREE.Mesh(
    new RoundedBoxGeometry(ENVELOPE_BASE_HALF.x * 2, ENVELOPE_BASE_HALF.y * 2, ENVELOPE_BASE_HALF.z * 2, 2, WIDGET_EDGE_RADIUS),
    widgetPartMaterial(WIDGET_BASE_COLOR, preview),
  );
  const controls: Record<string, THREE.Object3D> = {};
  const values = [obj.params.x, obj.params.y, obj.params.z, obj.params.w];
  const layout = envelopeKnobLayout();
  for (let i = 0; i < layout.length; i++) {
    const cap = buildKnobControl(layout[i].param, values[i], preview);
    cap.position.set(layout[i].x, layout[i].y, ENVELOPE_BASE_HALF.z);
    base.add(cap);
    controls[layout[i].param] = cap;
  }
  base.userData.knobControls = controls;
  const panel = new THREE.Mesh(
    new THREE.PlaneGeometry((ENVELOPE_CURVE_HALF_W + ENVELOPE_CURVE_PANEL_PAD) * 2, (ENVELOPE_CURVE_Y1 - ENVELOPE_CURVE_Y0) + ENVELOPE_CURVE_PANEL_PAD * 2),
    new THREE.MeshBasicMaterial({
      color: preview ? PREVIEW_COLOR : 0x000000,
      transparent: preview,
      opacity: preview ? 0.5 : 1,
      depthTest: false,
      depthWrite: false,
    }),
  );
  panel.position.set(0, (ENVELOPE_CURVE_Y0 + ENVELOPE_CURVE_Y1) / 2, ENVELOPE_PANEL_Z);
  panel.renderOrder = 10;
  base.add(panel);
  const gridGeom = new THREE.BufferGeometry();
  const gridArr = new Float32Array(ENVELOPE_GRID_MAX_LINES * 6);
  const gridCount = envelopeGridPositions(obj.params, gridArr);
  gridGeom.setAttribute("position", new THREE.BufferAttribute(gridArr, 3));
  gridGeom.setDrawRange(0, gridCount * 2);
  const grid = new THREE.LineSegments(
    gridGeom,
    new THREE.LineBasicMaterial({ color: preview ? PREVIEW_COLOR : ENVELOPE_GRID_COLOR, depthTest: false, depthWrite: false }),
  );
  grid.renderOrder = 11;
  base.add(grid);
  base.userData.envelopeGrid = grid;
  const curveGeom = new THREE.BufferGeometry();
  curveGeom.setAttribute("position", new THREE.BufferAttribute(envelopeCurvePositions(obj.params), 3));
  const curve = new THREE.Line(
    curveGeom,
    new THREE.LineBasicMaterial({ color: preview ? PREVIEW_COLOR : ENVELOPE_CURVE_COLOR, depthTest: false, depthWrite: false }),
  );
  curve.renderOrder = 12;
  base.add(curve);
  base.userData.envelopeCurve = curve;
  base.castShadow = !preview;
  base.receiveShadow = !preview;
  base.position.copy(obj.position);
  base.quaternion.copy(obj.rotation);
  return base;
}

export const PITCH_QUANTIZE_BASE_HALF = new THREE.Vector3(2 * WIDGET_UNIT, 1 * WIDGET_UNIT, 0.09);
const PQ_KEY_FIELD_HALF_W = 1.45;
const PQ_WHITE_HALF_H = 0.61;
const PQ_BLACK_HALF_H = 0.37;
const PQ_KEY_GAP = 0.04;
const PQ_KEY_THICK = 0.08;
const PQ_BLACK_KEY_THICK = PQ_KEY_THICK * 2;
const PQ_KEY_RADIUS = 0.025;
const PQ_BLACK_KEY_Y_NUDGE = 0.05;
const PQ_WHITE_KEY_Z = PITCH_QUANTIZE_BASE_HALF.z + PQ_KEY_THICK / 2;
const PQ_BLACK_KEY_Z = PITCH_QUANTIZE_BASE_HALF.z + PQ_BLACK_KEY_THICK / 2;
const PQ_WHITE_CLASSES: readonly number[] = [0, 2, 4, 5, 7, 9, 11];
const PQ_BLACK_CLASSES: readonly [number, number][] = [[1, 0], [3, 1], [6, 3], [8, 4], [10, 5]];

function buildPitchQuantizeKey(pitchClass: number, on: boolean, black: boolean, w: number, h: number, depth: number, preview: boolean): THREE.Mesh {
  const color = on ? (black ? PITCH_KEY_BLACK_ON_COLOR : PITCH_KEY_ON_COLOR) : black ? PITCH_KEY_BLACK_COLOR : PITCH_KEY_WHITE_COLOR;
  const key = new THREE.Mesh(new RoundedBoxGeometry(w, h, depth, 2, PQ_KEY_RADIUS), widgetPartMaterial(color, preview));
  key.userData.widgetControl = true;
  key.userData.knobParam = `pc${pitchClass}`;
  key.castShadow = !preview;
  key.receiveShadow = !preview;
  return key;
}

/**
 * PitchQuantize widget: a base plate (the selection / drag body, the root mesh)
 * carrying a 12-key piano keyboard — 7 white + 5 black keys, each a
 * `widgetControl`-tagged box whose `knobParam` is `pc<class>`. A key lit in the
 * ON color means its pitch class is allowed by the mask (params.x, see
 * pitchQuantizeMask); a tap toggles that bit and rebuilds the mesh. Black keys
 * sit forward of the whites so a click on an overlap resolves to the black key;
 * their extra-thick geometry (`PQ_BLACK_KEY_THICK`) reaches all the way down
 * to the base plate so they never float above the white keys with a visible
 * gap underneath. Non-collider; never enters the collision SDF.
 */
function buildPitchQuantizeMesh(obj: PlacedObject, preview: boolean): THREE.Mesh {
  const base = new THREE.Mesh(
    new RoundedBoxGeometry(PITCH_QUANTIZE_BASE_HALF.x * 2, PITCH_QUANTIZE_BASE_HALF.y * 2, PITCH_QUANTIZE_BASE_HALF.z * 2, 2, WIDGET_EDGE_RADIUS),
    widgetPartMaterial(WIDGET_BASE_COLOR, preview),
  );
  const mask = pitchQuantizeMask(obj);
  const cellW = (2 * PQ_KEY_FIELD_HALF_W) / PQ_WHITE_CLASSES.length;
  for (let i = 0; i < PQ_WHITE_CLASSES.length; i++) {
    const pc = PQ_WHITE_CLASSES[i];
    const on = ((mask >> pc) & 1) === 1;
    const key = buildPitchQuantizeKey(pc, on, false, cellW - PQ_KEY_GAP, PQ_WHITE_HALF_H * 2, PQ_KEY_THICK, preview);
    key.position.set(-PQ_KEY_FIELD_HALF_W + (i + 0.5) * cellW, 0, PQ_WHITE_KEY_Z);
    base.add(key);
  }
  for (const [pc, leftWhite] of PQ_BLACK_CLASSES) {
    const on = ((mask >> pc) & 1) === 1;
    const key = buildPitchQuantizeKey(pc, on, true, cellW * 0.62, PQ_BLACK_HALF_H * 2, PQ_BLACK_KEY_THICK, preview);
    key.position.set(-PQ_KEY_FIELD_HALF_W + (leftWhite + 1) * cellW, PQ_WHITE_HALF_H - PQ_BLACK_HALF_H + PQ_BLACK_KEY_Y_NUDGE, PQ_BLACK_KEY_Z);
    base.add(key);
  }
  base.castShadow = !preview;
  base.receiveShadow = !preview;
  base.position.copy(obj.position);
  base.quaternion.copy(obj.rotation);
  return base;
}

const SEQ_CELL_PITCH = 0.17;
const ARRAY_BASE_W = 3 * WIDGET_UNIT - SEQ_FRAME_W * 2;
const ARRAY_BASE_H = 2 * WIDGET_UNIT - SEQ_FRAME_W * 2;
const CURVE_WIDTH = 4 * WIDGET_UNIT - SEQ_FRAME_W * 2;
const CURVE_HEIGHT = 2 * WIDGET_UNIT - SEQ_FRAME_W * 2;
const CURVE_OVERLAY_Z = 0.01;

/** Visible row count of an array grid, clamped so an extreme min/max range cannot explode the instance count. */
function arrayRows(obj: PlacedObject): number {
  return Math.min(ARRAY_ROWS_MAX, Math.max(1, Math.round(obj.seqMax - obj.seqMin) + 1));
}

/**
 * Per-axis size scale of an array / curve widget, read off params (x = width,
 * y = height) so the button aspect ratio is freely stretchable. A 0 (legacy /
 * empty params) reads as the unit scale 1; the gizmo clamps live scales away
 * from 0.
 */
export function seqScale(obj: PlacedObject): { x: number; y: number } {
  return { x: obj.params.x || 1, y: obj.params.y || 1 };
}

/**
 * Half-extents of an array / curve widget at unit scale (params 1,1), the base the
 * top-right scale handle divides the cursor distance by to read a new per-axis
 * scale. Array scales with its column/row count; curve is a fixed strip.
 */
export function seqBaseHalfExtent(obj: PlacedObject): { w: number; h: number } {
  if (obj.type === "curve") {
    return { w: CURVE_WIDTH / 2, h: CURVE_HEIGHT / 2 };
  }
  return { w: ARRAY_BASE_W / 2, h: ARRAY_BASE_H / 2 };
}

export const SEQ_MIN_W_UNITS = 2;
export const SEQ_MIN_H_UNITS = 1;
export const SEQ_MAX_W_UNITS = 8;
export const SEQ_MAX_H_UNITS = 6;

/**
 * Maps a dragged outer half-extent of an array / curve widget to the per-axis
 * scale (params.x/y) whose realized outer size (frame included) is a whole number
 * of grid units on each axis, clamped to the seq unit range. Idempotent: feeding
 * back the outer half-extent this scale realizes returns the same scale.
 */
export function seqUnitScalePatch(obj: PlacedObject, hw: number, hh: number): { x: number; y: number } {
  const base = seqBaseHalfExtent(obj);
  const wUnits = Math.min(SEQ_MAX_W_UNITS, Math.max(SEQ_MIN_W_UNITS, Math.round((hw * 2) / WIDGET_UNIT)));
  const hUnits = Math.min(SEQ_MAX_H_UNITS, Math.max(SEQ_MIN_H_UNITS, Math.round((hh * 2) / WIDGET_UNIT)));
  return {
    x: ((wUnits * WIDGET_UNIT) / 2 - SEQ_FRAME_W) / base.w,
    y: ((hUnits * WIDGET_UNIT) / 2 - SEQ_FRAME_W) / base.h,
  };
}

/**
 * Snaps a widget's stored size to the unit grid on load: round widgets
 * (button/lamp/knob) to a whole-unit diameter (scale = unit count), array/curve to
 * a whole-unit outer size. Scenes saved with a continuous scale land on the nearest
 * grid size — a small visible size change is accepted. No-op / idempotent for
 * already-aligned sizes and every other type.
 */
export function snapWidgetParamsToUnits(type: ObjectType, params: THREE.Vector4): void {
  if (type === "button" || type === "lamp" || type === "knob") {
    const s = params.x > 0 ? params.x : WIDGET_DEFAULT_SCALE;
    params.x = Math.min(WIDGET_MAX_UNITS, Math.max(1, Math.round(s)));
    return;
  }
  if (type === "array" || type === "curve") {
    const base = type === "curve" ? { w: CURVE_WIDTH / 2, h: CURVE_HEIGHT / 2 } : { w: ARRAY_BASE_W / 2, h: ARRAY_BASE_H / 2 };
    const sx = params.x || 1;
    const sy = params.y || 1;
    const wUnits = Math.min(SEQ_MAX_W_UNITS, Math.max(SEQ_MIN_W_UNITS, Math.round(((base.w * sx + SEQ_FRAME_W) * 2) / WIDGET_UNIT)));
    const hUnits = Math.min(SEQ_MAX_H_UNITS, Math.max(SEQ_MIN_H_UNITS, Math.round(((base.h * sy + SEQ_FRAME_W) * 2) / WIDGET_UNIT)));
    params.x = ((wUnits * WIDGET_UNIT) / 2 - SEQ_FRAME_W) / base.w;
    params.y = ((hUnits * WIDGET_UNIT) / 2 - SEQ_FRAME_W) / base.h;
  }
}

/** Width/height of a display panel in grid units, read off params (x = width, y = height), integer-clamped to the allowed unit range. A legacy 0 promotes to the default. */
export function displayDims(obj: PlacedObject): { wUnits: number; hUnits: number } {
  return {
    wUnits: Math.min(DISPLAY_MAX_W_UNITS, Math.max(DISPLAY_MIN_W_UNITS, Math.round(obj.params.x || DISPLAY_DEFAULT_W_UNITS))),
    hUnits: Math.min(DISPLAY_MAX_H_UNITS, Math.max(DISPLAY_MIN_H_UNITS, Math.round(obj.params.y || DISPLAY_DEFAULT_H_UNITS))),
  };
}

/**
 * Outer half-extents (frame included) of a display panel for a unit size: each
 * unit is WIDGET_UNIT world, so the panel is an integer-unit rectangle and its
 * corner (the resize handle's ride) lands on the shared widget grid.
 */
export function displayPanelHalfExtentFor(wUnits: number, hUnits: number): { hw: number; hh: number } {
  return { hw: (wUnits * WIDGET_UNIT) / 2, hh: (hUnits * WIDGET_UNIT) / 2 };
}

export function displayPanelHalfExtent(obj: PlacedObject): { hw: number; hh: number } {
  const { wUnits, hUnits } = displayDims(obj);
  return displayPanelHalfExtentFor(wUnits, hUnits);
}

/**
 * Inverse of displayPanelHalfExtent: a dragged outer half-extent maps back to
 * integer width/height unit counts (both rounded to the WIDGET_UNIT grid and
 * clamped), so a resize steps the panel in whole units on each axis.
 */
export function displaySizePatch(hw: number, hh: number): { wUnits: number; hUnits: number } {
  return {
    wUnits: Math.min(DISPLAY_MAX_W_UNITS, Math.max(DISPLAY_MIN_W_UNITS, Math.round((hw * 2) / WIDGET_UNIT))),
    hUnits: Math.min(DISPLAY_MAX_H_UNITS, Math.max(DISPLAY_MIN_H_UNITS, Math.round((hh * 2) / WIDGET_UNIT))),
  };
}

/**
 * Geometry of the seven-segment glyph block for a display of the given unit size:
 * the black screen fills the panel inside its frame, the digit height follows the
 * screen height, digit width the fixed aspect, and the slot count is however many
 * gapped digit pitches fit the screen width (at least 1). The digit count is
 * derived from the panel width rather than stored, so the whole size lives in the
 * two unit params.
 */
function displayGlyphMetrics(wUnits: number, hUnits: number): { digits: number; digitH: number; digitW: number; pitch: number; screenW: number; screenH: number } {
  const screenW = wUnits * WIDGET_UNIT - DISPLAY_FRAME_W * 2;
  const screenH = hUnits * WIDGET_UNIT - DISPLAY_FRAME_W * 2;
  const digitH = screenH;
  const digitW = digitH * DISPLAY_DIGIT_ASPECT;
  const pitch = digitW * DISPLAY_DIGIT_GAP;
  const pad = digitW * DISPLAY_SIDE_PAD_FRAC;
  const digits = Math.max(1, Math.floor((screenW - pad * 2) / pitch));
  return { digits, digitH, digitW, pitch, screenW, screenH };
}

/**
 * Repaints an array grid's per-cell colors from its current values and cursor:
 * the cell whose row equals a column's stored value lights ON, the cursor column
 * is tinted, and the ON cell under the cursor blends the ON and cursor colors so
 * the two highlights read together. O(rows*cols), called on edit and whenever the
 * cursor column changes (not every frame).
 */
function paintArrayCells(root: THREE.Mesh, obj: PlacedObject, cursorCol: number): void {
  const grid = root.userData.seqGrid as THREE.InstancedMesh | undefined;
  if (!grid) {
    return;
  }
  const rows = root.userData.seqRows as number;
  const cols = root.userData.seqCols as number;
  const tmp = new THREE.Color();
  for (let col = 0; col < cols; col++) {
    const value = obj.seqValues[col] ?? obj.seqMin;
    const activeRow = Math.min(rows - 1, Math.max(0, Math.round(value - obj.seqMin)));
    const isCursor = col === cursorCol;
    for (let row = 0; row < rows; row++) {
      if (row === activeRow) {
        tmp.copy(SEQ_CELL_ON_COLOR);
        if (isCursor) {
          tmp.lerp(SEQ_CURSOR_COLOR, 0.5);
        }
      } else {
        tmp.copy(isCursor ? SEQ_CURSOR_COLOR : SEQ_CELL_OFF_COLOR);
      }
      grid.setColorAt(col * rows + row, tmp);
    }
  }
  if (grid.instanceColor) {
    grid.instanceColor.needsUpdate = true;
  }
}

/**
 * Array sequencer widget: an invisible plane (the root mesh: selection body and
 * the local-coordinate raycast target the cell pick inverts) holding a single
 * InstancedMesh of (rows × cols) cell quads. The grid is one mesh regardless of
 * cell count so thousands of cells stay one draw call; the pointer resolves a tap
 * to (col,row) from the plane's local hit point, not per-cell raycasts.
 */
/** Grid-boundary LineSegments (cols+1 vertical + rows+1 horizontal) drawn over the cells as 1px cell borders. */
function buildArrayBorders(cols: number, rows: number, pitchX: number, pitchY: number, gridW: number, gridH: number): THREE.LineSegments {
  const positions: number[] = [];
  for (let c = 0; c <= cols; c++) {
    const x = c * pitchX - gridW / 2;
    positions.push(x, -gridH / 2, 0, x, gridH / 2, 0);
  }
  for (let r = 0; r <= rows; r++) {
    const y = r * pitchY - gridH / 2;
    positions.push(-gridW / 2, y, 0, gridW / 2, y, 0);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  const lines = new THREE.LineSegments(geometry, new THREE.LineBasicMaterial({ color: SEQ_BORDER_COLOR }));
  lines.position.z = 0.01;
  lines.renderOrder = 1;
  return lines;
}

function buildArrayMesh(obj: PlacedObject, preview: boolean): THREE.Mesh {
  const rows = arrayRows(obj);
  const cols = Math.max(1, obj.seqLen);
  const { x: sx, y: sy } = seqScale(obj);
  const gridW = ARRAY_BASE_W * sx;
  const gridH = ARRAY_BASE_H * sy;
  const pitchX = gridW / cols;
  const pitchY = gridH / rows;
  const plateGeom = new RoundedBoxGeometry(gridW + SEQ_FRAME_W * 2, gridH + SEQ_FRAME_W * 2, SEQ_PLATE_DEPTH, 2, WIDGET_EDGE_RADIUS);
  plateGeom.translate(0, 0, -SEQ_PLATE_DEPTH / 2);
  const root = new THREE.Mesh(plateGeom, widgetPartMaterial(WIDGET_BASE_COLOR, preview));
  root.castShadow = !preview;
  root.receiveShadow = !preview;
  const interior = new THREE.Mesh(
    new THREE.PlaneGeometry(gridW, gridH),
    new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide }),
  );
  interior.position.z = 0.005;
  interior.userData.widgetControl = true;
  root.add(interior);
  const grid = new THREE.InstancedMesh(
    new THREE.PlaneGeometry(pitchX, pitchY),
    new THREE.MeshBasicMaterial({ transparent: preview, opacity: preview ? 0.5 : 1, side: THREE.DoubleSide }),
    rows * cols,
  );
  const m = new THREE.Matrix4();
  for (let col = 0; col < cols; col++) {
    for (let row = 0; row < rows; row++) {
      m.setPosition((col + 0.5) * pitchX - gridW / 2, (row + 0.5) * pitchY - gridH / 2, 0.01);
      grid.setMatrixAt(col * rows + row, m);
    }
  }
  grid.instanceMatrix.needsUpdate = true;
  root.add(grid);
  root.add(buildArrayBorders(cols, rows, pitchX, pitchY, gridW, gridH));
  root.userData.seqGrid = grid;
  root.userData.seqInterior = interior;
  root.userData.seqRows = rows;
  root.userData.seqCols = cols;
  root.userData.seqPitchX = pitchX;
  root.userData.seqPitchY = pitchY;
  root.position.copy(obj.position);
  root.quaternion.copy(obj.rotation);
  if (!preview) {
    paintArrayCells(root, obj, 0);
  }
  return root;
}

/** Maps a curve cell value to its local Y on a strip of height `h`; a zero-range curve pins to the baseline. */
function curveValueToY(value: number, min: number, max: number, h: number): number {
  const span = max - min;
  if (span === 0) {
    return -h / 2;
  }
  return ((value - min) / span - 0.5) * h;
}

function curveLinePositions(obj: PlacedObject): Float32Array {
  const cols = Math.max(1, obj.seqLen);
  const { x: sx, y: sy } = seqScale(obj);
  const w = CURVE_WIDTH * sx;
  const h = CURVE_HEIGHT * sy;
  const arr = new Float32Array(cols * 3);
  for (let i = 0; i < cols; i++) {
    const x = cols === 1 ? 0 : (i / (cols - 1) - 0.5) * w;
    arr[i * 3] = x;
    arr[i * 3 + 1] = curveValueToY(obj.seqValues[i] ?? obj.seqMin, obj.seqMin, obj.seqMax, h);
    arr[i * 3 + 2] = CURVE_OVERLAY_Z;
  }
  return arr;
}

/** Rewrites the curve polyline vertices from the node's current values, without a mesh rebuild. */
function repaintCurveLine(root: THREE.Mesh, obj: PlacedObject): void {
  const line = root.userData.curveLine as THREE.Line | undefined;
  if (!line) {
    return;
  }
  const attr = line.geometry.getAttribute("position") as THREE.BufferAttribute;
  attr.array.set(curveLinePositions(obj));
  attr.needsUpdate = true;
  line.geometry.computeBoundingSphere();
}

/**
 * Curve sequencer widget: an invisible plane (root mesh: selection body and the
 * paint raycast target) backing a dim face plate and an overlaid polyline of the
 * seqLen sample heights, plus a thin cursor marker. Dragging the face paints the
 * sample under the finger; the line vertices are rewritten in place on edit
 * rather than rebuilt.
 */
function buildCurveMesh(obj: PlacedObject, preview: boolean): THREE.Mesh {
  const { x: sx, y: sy } = seqScale(obj);
  const w = CURVE_WIDTH * sx;
  const h = CURVE_HEIGHT * sy;
  const plateGeom = new RoundedBoxGeometry(w + SEQ_FRAME_W * 2, h + SEQ_FRAME_W * 2, SEQ_PLATE_DEPTH, 2, WIDGET_EDGE_RADIUS);
  plateGeom.translate(0, 0, -SEQ_PLATE_DEPTH / 2);
  const root = new THREE.Mesh(plateGeom, widgetPartMaterial(WIDGET_BASE_COLOR, preview));
  root.castShadow = !preview;
  root.receiveShadow = !preview;
  const face = new THREE.Mesh(
    new THREE.PlaneGeometry(w, h),
    preview
      ? new THREE.MeshBasicMaterial({
          color: SEQ_CELL_OFF_COLOR,
          transparent: true,
          opacity: 0.4,
          depthWrite: false,
          depthTest: false,
          side: THREE.DoubleSide,
        })
      : new THREE.MeshBasicMaterial({ color: SEQ_CELL_OFF_COLOR, depthWrite: false, depthTest: false, side: THREE.DoubleSide }),
  );
  face.position.z = CURVE_OVERLAY_Z;
  face.renderOrder = 10;
  face.userData.widgetControl = true;
  root.add(face);
  const lineGeom = new THREE.BufferGeometry();
  lineGeom.setAttribute("position", new THREE.BufferAttribute(curveLinePositions(obj), 3));
  const line = new THREE.Line(lineGeom, new THREE.LineBasicMaterial({ color: CURVE_LINE_COLOR, depthTest: false, depthWrite: false }));
  line.renderOrder = 11;
  root.add(line);
  const cursor = new THREE.Mesh(
    new THREE.PlaneGeometry(Math.max(SEQ_CELL_PITCH * 0.5, w / Math.max(1, obj.seqLen)), h),
    new THREE.MeshBasicMaterial({ color: SEQ_CURSOR_COLOR, transparent: true, opacity: 0.5, depthWrite: false, depthTest: false }),
  );
  cursor.position.z = CURVE_OVERLAY_Z;
  cursor.renderOrder = 12;
  root.add(cursor);
  root.userData.curveLine = line;
  root.userData.curveCursor = cursor;
  root.userData.seqW = w;
  root.userData.seqH = h;
  root.position.copy(obj.position);
  root.quaternion.copy(obj.rotation);
  return root;
}

const DISPLAY_SEG_PER_DIGIT = 8;
const DISPLAY_GLYPH_FILL = 0.92;

interface DisplaySeg {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  thick: number;
}

/**
 * Local-space lamp specs for one digit's eight segments in the SEG_PATTERNS order
 * [a, b, c, d, e, f, g, DP], centered on the digit. Built from the six skeleton
 * nodes of a seven-segment glyph — TL/TR (top), ML/MR (middle), BL/BR (bottom):
 * each segment is the bar between two adjacent nodes with both ends pulled in by a
 * uniform gap `g`, so every junction (corners and the middle T) shares the same
 * gap. The decimal point is a small dot just right of the bottom-right node.
 */
function displaySegmentLayout(digitW: number, digitH: number): DisplaySeg[] {
  const t = digitH * 0.12;
  const g = t * 0.175;
  const w = digitW / 2 - t / 2;
  const h = digitH / 2 - t / 2;
  const seg = (x1: number, y1: number, x2: number, y2: number): DisplaySeg => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy);
    const ux = dx / len;
    const uy = dy / len;
    return { ax: x1 + ux * g, ay: y1 + uy * g, bx: x2 - ux * g, by: y2 - uy * g, thick: t };
  };
  return [
    seg(-w, h, w, h),
    seg(w, h, w, 0),
    seg(w, 0, w, -h),
    seg(-w, -h, w, -h),
    seg(-w, 0, -w, -h),
    seg(-w, h, -w, 0),
    seg(-w, 0, w, 0),
    { ax: w + t * 0.8, ay: -h, bx: w + t * 0.8, by: -h, thick: t },
  ];
}

/**
 * Pushes one lamp into the shared position/index arrays at vertex offset `base`,
 * shifting it by `cx` along x. A degenerate (zero-length) segment is rendered as
 * a `thick`-wide diamond (the decimal point); otherwise it is an elongated
 * hexagon — a bar between A and B tapering to a point at each end (the classic
 * seven-segment shape), the taper being half the thickness oriented from the A→B
 * direction so any segment angle works. Both forms emit six vertices in the same
 * triangle-fan layout so the per-segment color indexing stays uniform.
 */
function pushDisplaySegment(positions: number[], indices: number[], base: number, seg: DisplaySeg, cx: number, z: number): void {
  const ax = seg.ax + cx;
  const bx = seg.bx + cx;
  const dx = bx - ax;
  const dy = seg.by - seg.ay;
  const len = Math.hypot(dx, dy);
  const ht = seg.thick / 2;
  let pts: [number, number][];
  if (len < 1e-4) {
    pts = [
      [ax, seg.ay + ht],
      [ax + ht, seg.ay],
      [ax, seg.ay - ht],
      [ax, seg.ay - ht],
      [ax - ht, seg.ay],
      [ax, seg.ay + ht],
    ];
  } else {
    const ux = dx / len;
    const uy = dy / len;
    const px = -uy;
    const py = ux;
    pts = [
      [ax, seg.ay],
      [ax + ux * ht + px * ht, seg.ay + uy * ht + py * ht],
      [bx - ux * ht + px * ht, seg.by - uy * ht + py * ht],
      [bx, seg.by],
      [bx - ux * ht - px * ht, seg.by - uy * ht - py * ht],
      [ax + ux * ht - px * ht, seg.ay + uy * ht - py * ht],
    ];
  }
  for (const [vx, vy] of pts) {
    positions.push(vx, vy, z);
  }
  indices.push(base, base + 1, base + 2, base, base + 2, base + 3, base, base + 3, base + 4, base, base + 4, base + 5);
}

/**
 * Repaints a display panel's lamp colors for `value`: the digits decompose via
 * decomposeDisplay (odometer wrap, fraction with a decimal point), lit segments
 * take the bright LED color and the rest the dim off color, written into the
 * segment-geometry's vertex colors. O(digits), called on a value change.
 */
function paintDisplaySegments(root: THREE.Mesh, value: number): void {
  const colorAttr = root.userData.displayColor as THREE.BufferAttribute | undefined;
  if (!colorAttr) {
    return;
  }
  const digits = root.userData.displayDigits as number;
  const { segments, dp } = decomposeDisplay(value, digits);
  const arr = colorAttr.array as Float32Array;
  const paint = (segIndex: number, color: THREE.Color): void => {
    const base = segIndex * 6 * 3;
    for (let v = 0; v < 6; v++) {
      arr[base + v * 3] = color.r;
      arr[base + v * 3 + 1] = color.g;
      arr[base + v * 3 + 2] = color.b;
    }
  };
  for (let d = 0; d < digits; d++) {
    const digit = segments[d];
    const pattern = digit >= 0 ? SEG_PATTERNS[digit] : null;
    for (let s = 0; s < 7; s++) {
      paint(d * DISPLAY_SEG_PER_DIGIT + s, pattern && pattern[s] ? DISPLAY_SEG_ON_COLOR : DISPLAY_SEG_OFF_COLOR);
    }
    paint(d * DISPLAY_SEG_PER_DIGIT + 7, dp[d] ? DISPLAY_SEG_ON_COLOR : DISPLAY_SEG_OFF_COLOR);
  }
  colorAttr.needsUpdate = true;
}

/**
 * Display widget: the seq-widget frame plate (array/curve color) inset with a
 * black rectangular screen, over it one merged hexagon-segment mesh (`digits` × 8
 * lamps in a single draw call). Panel size lives in params.x/y as whole grid
 * units (WIDGET_UNIT each); the digit count is derived from the width so the
 * whole size is the two unit params. The glyph block is inset from the screen so
 * a black margin frames it; lit/dim state rides vertex color, repainted on value
 * change rather than rebuilt.
 */
function buildDisplayMesh(obj: PlacedObject, preview: boolean): THREE.Mesh {
  const { wUnits, hUnits } = displayDims(obj);
  const { digits, digitH, digitW, pitch, screenW, screenH } = displayGlyphMetrics(wUnits, hUnits);
  const plateGeom = new RoundedBoxGeometry(screenW + DISPLAY_FRAME_W * 2, screenH + DISPLAY_FRAME_W * 2, SEQ_PLATE_DEPTH, 2, WIDGET_EDGE_RADIUS);
  plateGeom.translate(0, 0, -SEQ_PLATE_DEPTH / 2);
  const root = new THREE.Mesh(plateGeom, widgetPartMaterial(WIDGET_BASE_COLOR, preview));
  root.castShadow = !preview;
  root.receiveShadow = !preview;
  const screen = new THREE.Mesh(
    new THREE.PlaneGeometry(screenW, screenH),
    new THREE.MeshBasicMaterial({ color: DISPLAY_SCREEN_COLOR, transparent: preview, opacity: preview ? 0.5 : 1, side: THREE.DoubleSide }),
  );
  screen.position.z = 0.012;
  root.add(screen);
  const layout = displaySegmentLayout(digitW * DISPLAY_GLYPH_FILL, digitH * DISPLAY_GLYPH_FILL);
  const positions: number[] = [];
  const indices: number[] = [];
  for (let d = 0; d < digits; d++) {
    const cx = (d - (digits - 1) / 2) * pitch;
    for (let s = 0; s < DISPLAY_SEG_PER_DIGIT; s++) {
      pushDisplaySegment(positions, indices, (d * DISPLAY_SEG_PER_DIGIT + s) * 6, layout[s], cx, 0.03);
    }
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geom.setAttribute("color", new THREE.BufferAttribute(new Float32Array(positions.length), 3));
  geom.setIndex(indices);
  const segMesh = new THREE.Mesh(geom, new THREE.MeshBasicMaterial({ vertexColors: true, transparent: preview, opacity: preview ? 0.5 : 1, side: THREE.DoubleSide }));
  root.add(segMesh);
  root.userData.displayColor = geom.getAttribute("color");
  root.userData.displayDigits = digits;
  root.position.copy(obj.position);
  root.quaternion.copy(obj.rotation);
  paintDisplaySegments(root, 0);
  root.userData.displayValue = 0;
  return root;
}

/**
 * Small pickable icon for a value, count, timer, decay, timbre, delay, router or
 * global node: a rounded square base (matching the knob/button/lamp base) with
 * a flat unlit color plate centered on top, inset by a border of
 * `NODE_ICON_BORDER_FRAC` of the base size (green for value with its CSS2D
 * value label, cyan for timer, orange for decay, violet for timbre, pink for
 * delay, yellow-green for router, blue for global). Fixed color (pitch / scale
 * do not apply) and never enters the collision SDF. The mesh is the click
 * target the panel and selection ride on.
 */
function buildNodeMesh(obj: PlacedObject, preview: boolean): THREE.Mesh {
  const baseColor = obj.type === "timbre" ? TIMBRE_COLOR : obj.type === "timer" ? TIMER_COLOR : obj.type === "decay" ? DECAY_COLOR : obj.type === "delay" ? DELAY_COLOR : obj.type === "router" ? ROUTER_COLOR : obj.type === "global" ? GLOBAL_COLOR : VALUE_COLOR;
  const color = preview ? new THREE.Color(PREVIEW_COLOR) : baseColor.clone();
  const side = KNOB_BASE_RADIUS * 2;
  const inner = side * (1 - NODE_ICON_BORDER_FRAC * 2);
  const base = new THREE.Mesh(
    new RoundedBoxGeometry(side, side, KNOB_BASE_HEIGHT, 2, WIDGET_EDGE_RADIUS),
    widgetPartMaterial(WIDGET_BASE_COLOR, preview),
  );
  const block = new THREE.Mesh(
    new THREE.BoxGeometry(inner, inner, NODE_ICON_HALF * 0.05),
    new THREE.MeshBasicMaterial({ color, transparent: preview, opacity: preview ? 0.5 : 1, depthWrite: !preview }),
  );
  block.position.z = KNOB_BASE_HEIGHT / 2;
  base.add(block);
  base.castShadow = !preview;
  base.receiveShadow = !preview;
  base.position.copy(obj.position);
  base.quaternion.copy(obj.rotation);
  return base;
}

/**
 * Builds a semi-transparent ghost mesh for one serialized object entry (paste
 * drag preview). Returns null when the entry does not parse. The default
 * particle radius stands in for the live value.
 */
export function ghostMeshFromEntry(entry: unknown): THREE.Mesh | null {
  const obj = parsePlaced(entry);
  return obj ? buildMesh(obj, true, PARTICLE_WORLD_SIZE / 2, "pitch", "matte", (): number => 0) : null;
}

/**
 * `transposeFor` resolves the per-timbre semitone transpose for a sounding
 * body's timbre, so a body's color matches the note it actually plays. Rail
 * uses its own timbre catalog (RailTimbre), which no timbre node governs, so it
 * colors from its raw pitch index.
 */
function buildMesh(obj: PlacedObject, preview: boolean, particleRadius: number, colorSet: ColorSet, objectMaterial: ObjectMaterial, transposeFor: (timbreId: Timbre) => number): THREE.Mesh {
  if (obj.type === "orbit") {
    return buildOrbitMesh(obj, preview);
  }
  if (obj.type === "group") {
    return buildGroupMesh(obj);
  }
  if (obj.type === "calc" || obj.type === "count" || obj.type === "timer" || obj.type === "decay" || obj.type === "timbre" || obj.type === "delay" || obj.type === "router" || obj.type === "global") {
    return buildNodeMesh(obj, preview);
  }
  if (obj.type === "display") {
    return buildDisplayMesh(obj, preview);
  }
  if (obj.type === "pitchQuantize") {
    return buildPitchQuantizeMesh(obj, preview);
  }
  if (obj.type === "button") {
    return buildButtonMesh(obj, preview);
  }
  if (obj.type === "lamp") {
    return buildLampMesh(obj, preview);
  }
  if (obj.type === "knob") {
    return buildKnobMesh(obj, preview);
  }
  if (obj.type === "envelope") {
    return buildEnvelopeMesh(obj, preview);
  }
  if (obj.type === "array") {
    return buildArrayMesh(obj, preview);
  }
  if (obj.type === "curve") {
    return buildCurveMesh(obj, preview);
  }
  if (obj.type === "force") {
    return buildForceMesh(obj, preview);
  }
  if (obj.type === "rail") {
    return buildRailMesh(obj, preview, particleRadius, colorSet, objectMaterial);
  }
  const geometry = shellGeometry(obj);
  if (preview) {
    const previewMesh = new THREE.Mesh(
      geometry,
      new THREE.MeshBasicMaterial({
        color: PREVIEW_COLOR,
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    previewMesh.position.copy(obj.position);
    previewMesh.quaternion.copy(obj.rotation);
    return previewMesh;
  }
  const color = objectColor(effectivePitchIndex(obj.pitchIndex, obj.timbreId, transposeFor(obj.timbreId)), colorSet);
  const isFunnel = obj.type === "funnel";
  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({
      color,
      ...materialParams(objectMaterial),
      side: THREE.FrontSide,
      clippingPlanes: isFunnel ? [SECTION_PLANE] : null,
    }),
  );
  mesh.position.copy(obj.position);
  mesh.quaternion.copy(obj.rotation);
  mesh.castShadow = true;
  if (isFunnel) {
    const cap = new THREE.Mesh(
      funnelCapGeometry(obj.params, obj.arcDeg),
      new THREE.MeshStandardMaterial({
        color: color.clone(),
        ...materialParams(objectMaterial),
        side: THREE.DoubleSide,
      }),
    );
    cap.position.z = -CAP_Z_OFFSET;
    mesh.add(cap);
    mesh.userData.cap = cap;
    if (obj.arcDeg < 360) {
      const arcCap = new THREE.Mesh(
        funnelArcCapGeometry(obj.params, THREE.MathUtils.degToRad(obj.arcDeg) / 2),
        new THREE.MeshStandardMaterial({
          color: color.clone(),
          ...materialParams(objectMaterial),
          side: THREE.DoubleSide,
          clippingPlanes: [SECTION_PLANE],
        }),
      );
      mesh.add(arcCap);
      mesh.userData.arcCap = arcCap;
    }
  }
  return mesh;
}

export class ObjectField {
  readonly group: THREE.Group;
  private readonly objects: PlacedObject[];
  private readonly meshes: THREE.Mesh[];
  private readonly packedObjects: PlacedObject[];
  private readonly packedMeshes: THREE.Mesh[];
  private readonly uniforms: CollisionUniforms;
  private readonly livePose: Map<string, { pos: THREE.Vector3; quat: THREE.Quaternion }>;
  private readonly velocityById: Map<string, THREE.Vector3>;
  private readonly railSampleCache: Map<string, Float32Array>;
  private readonly railRowOwner: (string | null)[];
  private railSamplesRecomputed: boolean;
  private preview: { template: ObjectTemplate; position: THREE.Vector3; mesh: THREE.Mesh } | null;
  private selected: PlacedObject | null;
  private readonly lastSelectedIdByType: Map<ObjectType, string>;
  private sectionActive: boolean;
  private particleRadius: number;
  private colorSet: ColorSet;
  private readonly timbreTranspose: Record<Timbre, number>;
  private objectMaterial: ObjectMaterial;
  private readonly disabledTypes: Set<ObjectType>;
  private readonly alwaysIdx: number[];
  private readonly gridObjIdx: number[];
  private readonly gridPosX: number[];
  private readonly gridPosY: number[];
  private readonly gridRadius: number[];
  private readonly gridCellCount: Int32Array;
  private readonly gridCellStart: Int32Array;
  private readonly gridCellCursor: Int32Array;
  private gridIdxOverflowWarned: boolean;
  private undoRecorder: ((id: string) => void) | null = null;

  constructor(uniforms: CollisionUniforms) {
    this.group = new THREE.Group();
    this.objects = [];
    this.meshes = [];
    this.packedObjects = [];
    this.packedMeshes = [];
    this.uniforms = uniforms;
    this.livePose = new Map<string, { pos: THREE.Vector3; quat: THREE.Quaternion }>();
    this.velocityById = new Map<string, THREE.Vector3>();
    this.railSampleCache = new Map<string, Float32Array>();
    this.railRowOwner = new Array<string | null>(MAX_OBJECTS).fill(null);
    this.railSamplesRecomputed = false;
    this.preview = null;
    this.selected = null;
    this.lastSelectedIdByType = new Map<ObjectType, string>();
    this.sectionActive = false;
    this.particleRadius = PARTICLE_WORLD_SIZE / 2;
    this.colorSet = "pitch";
    this.timbreTranspose = {};
    this.objectMaterial = "matte";
    this.disabledTypes = new Set<ObjectType>();
    this.alwaysIdx = [];
    this.gridObjIdx = [];
    this.gridPosX = [];
    this.gridPosY = [];
    this.gridRadius = [];
    this.gridCellCount = new Int32Array(GRID_MAX_DIM * GRID_MAX_DIM);
    this.gridCellStart = new Int32Array(GRID_MAX_DIM * GRID_MAX_DIM);
    this.gridCellCursor = new Int32Array(GRID_MAX_DIM * GRID_MAX_DIM);
    this.gridIdxOverflowWarned = false;
  }

  /**
   * Tracks the live particle radius so rail grooves (whose gap derives from it)
   * rebuild their wall tubes when the ParticleSize slider moves; other meshes
   * are radius-independent and stay untouched.
   */
  setParticleRadius(radius: number): void {
    if (this.particleRadius === radius) {
      return;
    }
    this.particleRadius = radius;
    for (let i = 0; i < this.objects.length; i++) {
      if (this.objects[i].type === "rail") {
        this.rebuildMesh(i);
      }
    }
  }

  /**
   * Disables whole object types (feature-category gating): their meshes hide,
   * they leave the collision/force pack, and they stop being pickable, while the
   * objects themselves stay in the field and persist. The set survives restore()
   * because add() reads it, so undo/import keeps the gate.
   */
  setDisabledTypes(types: readonly ObjectType[]): void {
    this.disabledTypes.clear();
    for (const t of types) {
      this.disabledTypes.add(t);
    }
    for (let i = 0; i < this.objects.length; i++) {
      this.meshes[i].visible = !this.disabledTypes.has(this.objects[i].type);
    }
    this.pack();
  }

  setColorSet(colorSet: ColorSet): void {
    this.colorSet = colorSet;
    this.recolorAll();
  }

  /**
   * Lets the undo recorder capture an object's pre-mutation value the moment a
   * local edit touches it. Mutations on the remote-apply / undo-apply paths run
   * with the recorder suppressed, so only genuine local edits accumulate a step.
   */
  setUndoRecorder(recorder: (id: string) => void): void {
    this.undoRecorder = recorder;
  }

  /**
   * Per-timbre semitone transpose used only for display (mesh color, note
   * labels): pitched bodies of that timbre recolor at the shifted index while
   * kit timbres stay put (see effectivePitchIndex). The stored pitch index is
   * never rewritten, so the object panel's pitch slider keeps the unshifted
   * value. A timbre with no governing timbre node stays at 0.
   */
  setTimbreTranspose(timbre: Timbre, semitones: number): void {
    const rounded = Math.round(semitones);
    if (this.timbreTranspose[timbre] === rounded) {
      return;
    }
    this.timbreTranspose[timbre] = rounded;
    this.recolorAll();
  }

  timbreTransposeFor(timbre: Timbre): number {
    return this.timbreTranspose[timbre] ?? 0;
  }

  setObjectMaterial(material: ObjectMaterial): void {
    this.objectMaterial = material;
    this.recolorAll();
  }

  recolorAll(): void {
    for (let i = 0; i < this.meshes.length; i++) {
      this.recolorMesh(i);
    }
  }

  add(obj: PlacedObject): void {
    if (this.objects.length >= MAX_OBJECTS) {
      console.warn(`ObjectField: MAX_OBJECTS (${MAX_OBJECTS}) reached, ignoring placement`);
      return;
    }
    this.undoRecorder?.(obj.id);
    const mesh = buildMesh(obj, false, this.particleRadius, this.colorSet, this.objectMaterial, (timbreId: Timbre): number => this.timbreTransposeFor(timbreId));
    this.setCapVisible(mesh, this.sectionActive);
    mesh.visible = !this.disabledTypes.has(obj.type);
    this.objects.push(obj);
    this.meshes.push(mesh);
    this.group.add(mesh);
    this.pack();
  }

  clear(): void {
    this.deselect();
    for (const mesh of this.meshes) {
      this.group.remove(mesh);
      this.disposeMesh(mesh);
    }
    this.meshes.length = 0;
    this.objects.length = 0;
    this.livePose.clear();
    this.velocityById.clear();
    this.railSampleCache.clear();
    this.pack();
  }

  selectedIndex(): number {
    return this.selected ? this.objects.indexOf(this.selected) : -1;
  }

  selectIndex(idx: number): void {
    if (idx < 0 || idx >= this.objects.length) {
      this.deselect();
      return;
    }
    this.select(this.objects[idx]);
  }

  /**
   * Distance-ordered hits under the ray paired with their hit distance, front→
   * back. Front/back faces of one mesh dedupe (the nearer face's distance is
   * kept) so a convex solid contributes a single entry. The caller merges these
   * with the emitter field's hits to cycle selection across both types by
   * distance.
   */
  orderedHits(raycaster: THREE.Raycaster): { item: PlacedObject; dist: number }[] {
    const hits = raycaster.intersectObjects(this.meshes, false);
    const ordered: { item: PlacedObject; dist: number }[] = [];
    for (const h of hits) {
      const idx = this.meshes.indexOf(h.object as THREE.Mesh);
      if (idx < 0 || !this.meshes[idx].visible || this.disabledTypes.has(this.objects[idx].type)) {
        continue;
      }
      if (!ordered.some((o: { item: PlacedObject; dist: number }): boolean => o.item === this.objects[idx])) {
        ordered.push({ item: this.objects[idx], dist: h.distance });
      }
    }
    return ordered;
  }

  /**
   * World-space centers of every currently pickable body (visible mesh, type not
   * disabled), for the selection picker's screen-space tap fallback: a body that
   * projects to only a few pixels slips through the geometry raycast, so the
   * caller projects these centers and grabs the nearest within the min-tap
   * radius. Mesh world position is read (not `object.position`) so transform-
   * driven group/orbit members report their live on-screen center. Filter mirrors
   * `orderedHits` exactly so the fallback never offers a body the raycast wouldn't.
   */
  pickCandidates(): { item: PlacedObject; center: THREE.Vector3 }[] {
    const out: { item: PlacedObject; center: THREE.Vector3 }[] = [];
    for (let i = 0; i < this.objects.length; i++) {
      if (!this.meshes[i].visible || this.disabledTypes.has(this.objects[i].type)) {
        continue;
      }
      out.push({ item: this.objects[i], center: this.meshes[i].getWorldPosition(new THREE.Vector3()) });
    }
    return out;
  }

  select(obj: PlacedObject): void {
    const idx = this.objects.indexOf(obj);
    if (idx < 0) {
      return;
    }
    this.selected = obj;
    this.lastSelectedIdByType.set(obj.type, obj.id);
  }

  deselect(): void {
    this.selected = null;
  }

  selectedObject(): PlacedObject | null {
    return this.selected;
  }

  lastSelectedOfType(type: ObjectType): PlacedObject | null {
    const id = this.lastSelectedIdByType.get(type);
    if (id === undefined) {
      return null;
    }
    return this.objects.find((o: PlacedObject): boolean => o.id === id) ?? null;
  }

  moveSelected(position: THREE.Vector3): void {
    const obj = this.selected;
    if (!obj) {
      return;
    }
    this.moveById(obj.id, position);
  }

  moveById(id: string, position: THREE.Vector3): void {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0) {
      return;
    }
    this.undoRecorder?.(id);
    this.objects[idx].position.copy(position);
    this.meshes[idx].position.copy(position);
    this.pack();
  }

  editSelected(patch: Partial<ObjectTemplate>): void {
    const obj = this.selected;
    if (!obj) {
      return;
    }
    const idx = this.objects.indexOf(obj);
    if (idx < 0) {
      return;
    }
    this.undoRecorder?.(obj.id);
    let rebuild = false;
    if (patch.type !== undefined && patch.type !== obj.type) {
      obj.type = patch.type;
      obj.params = defaultParams(patch.type);
      rebuild = true;
    }
    if (patch.params !== undefined) {
      obj.params.copy(patch.params);
      rebuild = true;
    }
    if (patch.scale !== undefined) {
      obj.scale.copy(patch.scale);
      rebuild = true;
    }
    if (patch.arcDeg !== undefined) {
      obj.arcDeg = patch.arcDeg;
      rebuild = true;
    }
    if (patch.orbit !== undefined) {
      obj.orbit.rotSpeed.copy(patch.orbit.rotSpeed);
      obj.orbit.amp.copy(patch.orbit.amp);
      obj.orbit.periodPct.copy(patch.orbit.periodPct);
      obj.orbit.phasePct.copy(patch.orbit.phasePct);
      obj.orbit.clockSync = patch.orbit.clockSync;
      obj.orbit.play = patch.orbit.play;
    }
    if (patch.restitution !== undefined) {
      obj.restitution = patch.restitution;
    }
    if (patch.friction !== undefined) {
      obj.friction = patch.friction;
    }
    if (patch.mass !== undefined) {
      obj.mass = patch.mass;
    }
    if (patch.massEnabled !== undefined) {
      obj.massEnabled = patch.massEnabled;
    }
    if (patch.killOnContact !== undefined) {
      obj.killOnContact = patch.killOnContact;
    }
    if (patch.fade !== undefined) {
      obj.fade = patch.fade;
    }
    if (patch.operator !== undefined) {
      obj.operator = patch.operator;
    }
    if (patch.operand !== undefined) {
      obj.operand = patch.operand;
    }
    if (patch.decayS !== undefined) {
      obj.decayS = patch.decayS;
    }
    if (patch.delayS !== undefined) {
      obj.delayS = patch.delayS;
    }
    if (patch.routerHoldS !== undefined) {
      obj.routerHoldS = patch.routerHoldS;
    }
    if (patch.routerHoldResetOnPulse !== undefined) {
      obj.routerHoldResetOnPulse = patch.routerHoldResetOnPulse;
    }
    if (patch.timerMode !== undefined) {
      obj.timerMode = patch.timerMode;
    }
    if (patch.buttonMode !== undefined) {
      obj.buttonMode = patch.buttonMode;
      if (patch.buttonMode !== "toggle") {
        obj.buttonOn = false;
      }
      this.setButtonPressed(obj.id, obj.buttonMode === "toggle" && obj.buttonOn);
    }
    if (patch.buttonPulseOnReset !== undefined) {
      obj.buttonPulseOnReset = patch.buttonPulseOnReset;
    }
    if (patch.lampValue !== undefined) {
      obj.lampValue = patch.lampValue;
    }
    if (patch.knobMin !== undefined || patch.knobMax !== undefined || patch.knobInt !== undefined) {
      if (patch.knobMin !== undefined) {
        obj.knobMin = patch.knobMin;
      }
      if (patch.knobMax !== undefined) {
        obj.knobMax = patch.knobMax;
      }
      if (patch.knobInt !== undefined) {
        obj.knobInt = patch.knobInt;
      }
      if (patch.knobInt === true && obj.knobMax === KNOB_MAX_DEFAULT) {
        obj.knobMax = KNOB_INT_DEFAULT_MAX;
      }
      if (obj.knobInt) {
        obj.knobMin = Math.round(obj.knobMin);
        obj.knobMax = Math.round(obj.knobMax);
        obj.knobValue = snapKnobValue(obj.knobValue, obj.knobMin, obj.knobMax);
        if (obj.type === "knob") {
          this.setKnobIndicator(obj.id, "value", obj.knobValue);
        }
      }
    }
    if (patch.lampHue !== undefined || patch.lampSat !== undefined) {
      if (patch.lampHue !== undefined) {
        obj.lampHue = patch.lampHue;
      }
      if (patch.lampSat !== undefined) {
        obj.lampSat = patch.lampSat;
      }
      this.setLampColor(obj.id);
    }
    if (patch.seqMin !== undefined || patch.seqMax !== undefined || patch.seqLen !== undefined || patch.seqValues !== undefined) {
      if (patch.seqMin !== undefined) {
        obj.seqMin = patch.seqMin;
      }
      if (patch.seqMax !== undefined) {
        obj.seqMax = patch.seqMax;
      }
      if (patch.seqValues !== undefined) {
        obj.seqValues = [...patch.seqValues];
      }
      if (patch.seqLen !== undefined) {
        obj.seqLen = Math.max(1, Math.round(patch.seqLen));
      }
      const resized = new Array<number>(obj.seqLen);
      for (let i = 0; i < obj.seqLen; i++) {
        resized[i] = coerceSeqValue(obj.type, obj.seqValues[i] ?? obj.seqMin, obj.seqMin, obj.seqMax);
      }
      obj.seqValues = resized;
      rebuild = true;
    }
    if (patch.seqCycleLength !== undefined) {
      obj.seqCycleLength = Math.max(1, Math.round(patch.seqCycleLength));
    }
    if (patch.collisionEnabled !== undefined) {
      obj.collisionEnabled = patch.collisionEnabled;
    }
    if (patch.timbreId !== undefined) {
      obj.timbreId = patch.timbreId;
    }
    if (patch.timbreIds !== undefined) {
      obj.timbreIds = [...patch.timbreIds];
      if (obj.type === "timbre" && obj.timbreIds.length > 0) {
        obj.timbreId = obj.timbreIds[0];
      }
    }
    if (patch.railTimbre !== undefined) {
      obj.railTimbre = patch.railTimbre;
    }
    if (patch.volume !== undefined) {
      obj.volume = patch.volume;
    }
    if (patch.muted !== undefined) {
      obj.muted = patch.muted;
    }
    if (patch.name !== undefined) {
      obj.name = patch.name;
    }
    let recolor = false;
    if (patch.pitchIndex !== undefined) {
      obj.pitchIndex = patch.pitchIndex;
      recolor = true;
    }
    if (patch.pitchIndexEnd !== undefined) {
      obj.pitchIndexEnd = patch.pitchIndexEnd;
      recolor = true;
    }
    if (patch.rotation !== undefined) {
      obj.rotation.copy(patch.rotation);
    }
    if (rebuild) {
      this.rebuildMesh(idx);
    } else {
      if (recolor) {
        this.recolorMesh(idx);
      }
      if (patch.rotation !== undefined) {
        this.meshes[idx].quaternion.copy(obj.rotation);
      }
    }
    this.pack();
  }

  /**
   * Overwrites one object's pitch index by id and recolors its mesh, skipping
   * when unchanged so a per-frame Param-in drive costs nothing while idle. Used by
   * the signal graph (the selected-object path goes through editSelected).
   */
  setPitchIndex(id: string, pitchIndex: number): void {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0 || this.objects[idx].pitchIndex === pitchIndex) {
      return;
    }
    this.objects[idx].pitchIndex = pitchIndex;
    this.recolorMesh(idx);
  }

  /** Overwrites a rail's end pitch index by id and recolors (rail end gradient); skips when unchanged. */
  setPitchIndexEnd(id: string, pitchIndexEnd: number): void {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0 || this.objects[idx].pitchIndexEnd === pitchIndexEnd) {
      return;
    }
    this.objects[idx].pitchIndexEnd = pitchIndexEnd;
    this.recolorMesh(idx);
  }

  setTimbreById(id: string, timbreId: Timbre): void {
    const obj = this.objects.find((o: PlacedObject): boolean => o.id === id) ?? null;
    if (obj !== null) {
      obj.timbreId = timbreId;
    }
  }

  setRailTimbreById(id: string, railTimbre: RailTimbre): void {
    const obj = this.objects.find((o: PlacedObject): boolean => o.id === id) ?? null;
    if (obj !== null) {
      obj.railTimbre = railTimbre;
    }
  }

  updateRailPoints(id: string, newPoints: THREE.Vector2[]): void {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0) {
      return;
    }
    this.undoRecorder?.(id);
    this.objects[idx].railPoints = newPoints;
    this.railSampleCache.delete(id);
    this.rebuildMesh(idx);
    this.pack();
  }

  deleteSelected(): void {
    const obj = this.selected;
    if (!obj) {
      return;
    }
    const idx = this.objects.indexOf(obj);
    if (idx < 0) {
      return;
    }
    this.undoRecorder?.(obj.id);
    this.deselect();
    const mesh = this.meshes[idx];
    this.group.remove(mesh);
    this.disposeMesh(mesh);
    this.meshes.splice(idx, 1);
    this.objects.splice(idx, 1);
    this.pack();
  }

  removeById(id: string): void {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0) {
      return;
    }
    this.undoRecorder?.(id);
    if (this.selected === this.objects[idx]) {
      this.deselect();
    }
    const mesh = this.meshes[idx];
    this.group.remove(mesh);
    this.disposeMesh(mesh);
    this.meshes.splice(idx, 1);
    this.objects.splice(idx, 1);
    this.livePose.delete(id);
    this.velocityById.delete(id);
    this.railSampleCache.delete(id);
    this.pack();
  }

  /**
   * Replaces a placed object's data in place from its serialized form, keeping the
   * same array slot and object identity so a remote edit to a selected object does
   * not deselect it (closing the panel) — unlike removeById + restore. Per-id
   * caches are cleared like removeById so stale rail samples / velocity do not
   * linger. Adds a new object for an unknown id.
   */
  updateFromSerialized(id: string, entry: unknown): void {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0) {
      this.restore([entry]);
      return;
    }
    const parsed = parsePlaced(entry);
    if (parsed === null) {
      return;
    }
    Object.assign(this.objects[idx], parsed);
    this.livePose.delete(id);
    this.velocityById.delete(id);
    this.railSampleCache.delete(id);
    this.rebuildMesh(idx);
    this.pack();
  }

  beginPreview(template: ObjectTemplate, position: THREE.Vector3): void {
    this.cancelPreview();
    const spawnPosition = nodeSpawnPosition(template.type, position);
    const mesh = buildMesh(placedFromTemplate(template, spawnPosition), true, this.particleRadius, this.colorSet, this.objectMaterial, (timbreId: Timbre): number => this.timbreTransposeFor(timbreId));
    this.group.add(mesh);
    this.preview = {
      template: { ...template, params: template.params.clone() },
      position: spawnPosition.clone(),
      mesh,
    };
  }

  updatePreview(position: THREE.Vector3): void {
    if (!this.preview) {
      return;
    }
    const spawnPosition = nodeSpawnPosition(this.preview.template.type, position);
    this.preview.position.copy(spawnPosition);
    this.preview.mesh.position.copy(spawnPosition);
  }

  /**
   * A committed placement counts as the last-touched object of its type, so
   * the next spawn template (param copy and pitch cycle) continues from it.
   */
  commitPreview(): PlacedObject | null {
    if (!this.preview) {
      return null;
    }
    const { template, position } = this.preview;
    if (template.type === "global" && this.objects.some((o: PlacedObject): boolean => o.type === "global")) {
      this.cancelPreview();
      return null;
    }
    this.cancelPreview();
    const before = this.objects.length;
    const obj = placedFromTemplate(template, position);
    this.add(obj);
    if (this.objects.length <= before) {
      return null;
    }
    this.lastSelectedIdByType.set(obj.type, obj.id);
    track("first_place", { kind: obj.type });
    return obj;
  }

  list(): readonly PlacedObject[] {
    return this.objects;
  }

  meshAt(index: number): THREE.Mesh | null {
    return index >= 0 && index < this.meshes.length ? this.meshes[index] : null;
  }

  meshById(id: string): THREE.Mesh | null {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    return idx >= 0 ? this.meshes[idx] : null;
  }

  setSectionClip(active: boolean): void {
    SECTION_PLANE.constant = active ? 0 : SECTION_CLIP_OFF;
    this.sectionActive = active;
    for (const mesh of this.meshes) {
      this.setCapVisible(mesh, active);
    }
  }

  cancelPreview(): void {
    if (!this.preview) {
      return;
    }
    this.group.remove(this.preview.mesh);
    this.preview.mesh.geometry.dispose();
    (this.preview.mesh.material as THREE.Material).dispose();
    this.preview = null;
  }

  /**
   * Writes each object with only the fields whose value differs from the type's
   * default (`defaultPlaced`); id / type / position / rotation are always
   * written. A rail always writes its control points (its shape, never a
   * default). The parser fills every absent field from the same default table,
   * so the round-trip is lossless while unused / default-valued fields stay out
   * of the file.
   */
  serialize(): unknown {
    return this.objects.map((o: PlacedObject): Record<string, unknown> => this.serializeOne(o));
  }

  /** One object's serialized form by id (undo recorder's before-value capture), or null when absent. */
  serializeById(id: string): unknown | null {
    const o = this.objects.find((obj: PlacedObject): boolean => obj.id === id);
    return o ? this.serializeOne(o) : null;
  }

  private serializeOne(o: PlacedObject): Record<string, unknown> {
    {
      const d = defaultPlaced(o.type);
      const out: Record<string, unknown> = {
        id: o.id,
        type: o.type,
        x: f32(o.position.x),
        y: f32(o.position.y),
        z: f32(o.position.z),
      };
      if (o.rotation.x !== 0 || o.rotation.y !== 0 || o.rotation.z !== 0 || o.rotation.w !== 1) {
        out.qx = f32(o.rotation.x);
        out.qy = f32(o.rotation.y);
        out.qz = f32(o.rotation.z);
        out.qw = f32(o.rotation.w);
      }
      if (o.restitution !== d.restitution) out.restitution = f32(o.restitution);
      if (o.friction !== d.friction) out.friction = f32(o.friction);
      if (o.mass !== d.mass) out.mass = f32(o.mass);
      if (o.massEnabled !== d.massEnabled) out.massEnabled = o.massEnabled;
      if (o.killOnContact !== d.killOnContact) out.killOnContact = o.killOnContact;
      if (o.collisionEnabled !== d.collisionEnabled) out.collisionEnabled = o.collisionEnabled;
      if (o.pitchIndex !== d.pitchIndex) out.pitchIndex = o.pitchIndex;
      if (o.timbreId !== d.timbreId) out.timbreId = o.timbreId;
      if (o.type === "timbre") out.timbreIds = [...o.timbreIds];
      if (o.volume !== d.volume) out.volume = f32(o.volume);
      if (o.muted !== d.muted) out.muted = o.muted;
      if (o.fade !== d.fade) out.fade = f32(o.fade);
      if (o.operator !== d.operator) out.operator = o.operator;
      if (o.operand !== d.operand) out.operand = f32(o.operand);
      if (o.decayS !== d.decayS) out.decayS = f32(o.decayS);
      if (o.delayS !== d.delayS) out.delayS = f32(o.delayS);
      if (o.routerHoldS !== d.routerHoldS) out.routerHoldS = f32(o.routerHoldS);
      if (o.routerHoldResetOnPulse !== d.routerHoldResetOnPulse) out.routerHoldResetOnPulse = o.routerHoldResetOnPulse;
      if (o.timerMode !== d.timerMode) out.timerMode = o.timerMode;
      if (o.knobValue !== d.knobValue) out.knobValue = f32(o.knobValue);
      if (o.knobMin !== d.knobMin) out.knobMin = f32(o.knobMin);
      if (o.knobMax !== d.knobMax) out.knobMax = f32(o.knobMax);
      if (o.knobInt !== d.knobInt) out.knobInt = o.knobInt;
      if (o.buttonMode !== d.buttonMode) out.buttonMode = o.buttonMode;
      if (o.buttonOn !== d.buttonOn) out.buttonOn = o.buttonOn;
      if (o.buttonPulseOnReset !== d.buttonPulseOnReset) out.buttonPulseOnReset = o.buttonPulseOnReset;
      if (o.lampHue !== d.lampHue) out.lampHue = f32(o.lampHue);
      if (o.lampSat !== d.lampSat) out.lampSat = f32(o.lampSat);
      if (o.lampValue !== d.lampValue) out.lampValue = f32(o.lampValue);
      if (o.seqMin !== d.seqMin) out.seqMin = f32(o.seqMin);
      if (o.seqMax !== d.seqMax) out.seqMax = f32(o.seqMax);
      if (o.seqLen !== d.seqLen) out.seqLen = o.seqLen;
      if (o.type === "curve" && o.seqCycleLength !== d.seqCycleLength) out.seqCycleLength = o.seqCycleLength;
      if (!seqValuesEqual(o.seqValues, d.seqValues)) out.seqValues = o.seqValues.map(f32);
      if (o.params.x !== d.params.x) out.px = f32(o.params.x);
      if (o.params.y !== d.params.y) out.py = f32(o.params.y);
      if (o.params.z !== d.params.z) out.pz = f32(o.params.z);
      if (o.params.w !== d.params.w) out.pw = f32(o.params.w);
      if (o.scale.x !== d.scale.x) out.sx = f32(o.scale.x);
      if (o.scale.y !== d.scale.y) out.sy = f32(o.scale.y);
      if (o.arcDeg !== d.arcDeg) out.arcDeg = f32(o.arcDeg);
      if (o.railPoints.length > 0) {
        out.railPoints = o.railPoints.map((p: THREE.Vector2): number[] => [f32(p.x), f32(p.y)]);
      }
      if (o.pitchIndexEnd !== o.pitchIndex) out.pitchIndexEnd = o.pitchIndexEnd;
      if (o.railTimbre !== d.railTimbre) out.railTimbre = o.railTimbre;
      if (!orbitIsDefault(o.orbit)) {
        out.orbit = {
          rotSpeed: [f32(o.orbit.rotSpeed.x), f32(o.orbit.rotSpeed.y), f32(o.orbit.rotSpeed.z)],
          amp: [f32(o.orbit.amp.x), f32(o.orbit.amp.y), f32(o.orbit.amp.z)],
          periodPct: [f32(o.orbit.periodPct.x), f32(o.orbit.periodPct.y), f32(o.orbit.periodPct.z)],
          phasePct: [f32(o.orbit.phasePct.x), f32(o.orbit.phasePct.y), f32(o.orbit.phasePct.z)],
          clockSync: o.orbit.clockSync,
          play: o.orbit.play,
        };
      }
      if (o.name !== "") out.name = o.name;
      return out;
    }
  }

  restore(raw: unknown): void {
    if (!Array.isArray(raw)) {
      return;
    }
    for (const entry of raw) {
      const obj = parsePlaced(entry);
      if (obj) {
        this.add(obj);
      }
    }
  }

  private rebuildMesh(idx: number): void {
    const old = this.meshes[idx];
    this.group.remove(old);
    this.disposeMesh(old);
    const mesh = buildMesh(this.objects[idx], false, this.particleRadius, this.colorSet, this.objectMaterial, (timbreId: Timbre): number => this.timbreTransposeFor(timbreId));
    this.setCapVisible(mesh, this.sectionActive);
    this.meshes[idx] = mesh;
    this.group.add(mesh);
  }

  private recolorMesh(idx: number): void {
    const type = this.objects[idx].type;
    if (type === "calc" || type === "count" || type === "display" || type === "timer" || type === "decay" || type === "orbit" || type === "group" || type === "timbre" || type === "pitchQuantize" || type === "delay" || type === "router" || type === "button" || type === "lamp" || type === "knob" || type === "envelope" || type === "array" || type === "curve" || type === "global") {
      return;
    }
    if (type === "rail") {
      this.rebuildMesh(idx);
      return;
    }
    const mesh = this.meshes[idx];
    const color = objectColor(effectivePitchIndex(this.objects[idx].pitchIndex, this.objects[idx].timbreId, this.timbreTransposeFor(this.objects[idx].timbreId)), this.colorSet);
    const params = materialParams(this.objectMaterial);
    const material = mesh.material as THREE.MeshStandardMaterial;
    material.color.copy(color);
    applyMaterialParams(material, params);
    const cap = mesh.userData.cap as THREE.Mesh | undefined;
    if (cap) {
      const capMaterial = cap.material as THREE.MeshStandardMaterial;
      capMaterial.color.copy(color);
      applyMaterialParams(capMaterial, params);
    }
    const arcCap = mesh.userData.arcCap as THREE.Mesh | undefined;
    if (arcCap) {
      const arcCapMaterial = arcCap.material as THREE.MeshStandardMaterial;
      arcCapMaterial.color.copy(color);
      applyMaterialParams(arcCapMaterial, params);
    }
  }

  /**
   * Camera-facing half-depth a sphere/box must recede along -z in the 2D view
   * so its full rounded body sits behind the cut plane instead of being sliced
   * flat. Funnel returns 0 (it uses clip + section cap instead). Mesh-only — the
   * collision SDF keeps using the unshifted `obj.position`.
   */
  private setCapVisible(mesh: THREE.Mesh, visible: boolean): void {
    const cap = mesh.userData.cap as THREE.Mesh | undefined;
    if (cap) {
      cap.visible = visible;
    }
  }

  private disposeMesh(mesh: THREE.Mesh): void {
    for (const child of mesh.children) {
      if (child.userData.selectionFrame) {
        child.removeFromParent();
        continue;
      }
      if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
        child.geometry.dispose();
        (child.material as THREE.Material).dispose();
      }
    }
    mesh.geometry.dispose();
    (mesh.material as THREE.Material).dispose();
  }

  selectedMesh(): THREE.Mesh | null {
    if (!this.selected) {
      return null;
    }
    const idx = this.objects.indexOf(this.selected);
    return idx >= 0 ? this.meshes[idx] : null;
  }

  /**
   * Packs only collider objects into the collision uniforms as a dense sequence
   * (value / orbit / group nodes are skipped), and records the parallel packed→object and
   * packed→mesh maps so a `readContactCounts()` index k resolves to the right
   * entity. Without compaction, inserting a non-collider node into the object
   * array would shift the contact-buffer alignment and misattribute hits.
   * Column C's y carries the funnel arc half-angle in radians (π = full shell);
   * only `sdFunnel` reads it.
   *
   * uRailTex re-upload is gated: rail centerline texels are object-local and
   * pose-independent (the world transform is applied in the shader), so they only
   * change when a rail's samples are recomputed (cache miss) or a rail lands on a
   * different collision slot than last pack. needsUpdate is set only then, sparing
   * a full-texture upload every static frame on weak GPUs.
   */
  private pack(): void {
    const { uObjTex, uRailTex } = this.uniforms;
    const tex = uObjTex.value.image.data as Float32Array;
    const railData = uRailTex.value.image.data as Float32Array;
    this.packedObjects.length = 0;
    this.packedMeshes.length = 0;
    this.alwaysIdx.length = 0;
    this.gridObjIdx.length = 0;
    this.gridPosX.length = 0;
    this.gridPosY.length = 0;
    this.gridRadius.length = 0;
    this.railSamplesRecomputed = false;
    let railDirty = false;

    for (let i = 0; i < this.objects.length; i++) {
      const o = this.objects[i];
      if (!isCollider(o.type) || this.disabledTypes.has(o.type)) {
        continue;
      }
      const effectiveMass = o.massEnabled && o.type !== "force" ? o.mass : 0;
      const radius = this.colliderRadius(o);
      const large = Math.ceil((2 * radius) / GRID_CELL_SIZE) > GRID_LARGE_SPAN_CELLS;
      if (effectiveMass !== 0 || o.type === "force" || large) {
        this.alwaysIdx.push(i);
      } else {
        const live = this.livePose.get(o.id);
        const pos = live ? live.pos : o.position;
        this.gridObjIdx.push(i);
        this.gridPosX.push(pos.x);
        this.gridPosY.push(pos.y);
        this.gridRadius.push(radius);
      }
    }

    let k = 0;
    for (let a = 0; a < this.alwaysIdx.length; a++) {
      railDirty = this.writeColliderSlot(tex, railData, k, this.alwaysIdx[a]) || railDirty;
      k++;
    }
    const alwaysCount = k;
    for (let g = 0; g < this.gridObjIdx.length; g++) {
      railDirty = this.writeColliderSlot(tex, railData, k, this.gridObjIdx[g]) || railDirty;
      k++;
    }

    if (railDirty || this.railSamplesRecomputed) {
      uRailTex.value.needsUpdate = true;
    }
    uObjTex.value.needsUpdate = true;
    this.uniforms.uObjCount.value = k;
    this.uniforms.uAlwaysCount.value = alwaysCount;
    this.buildGrid(alwaysCount);
  }

  /**
   * Writes one collider into uObjTex row k (the six A..F vec4 slots) and, for a
   * rail, its sampled center line into uRailTex row k. Returns whether the rail
   * row's owner changed, so the caller can gate the rail-texture re-upload. Slot
   * order is set by pack(): always-test colliders fill [0, uAlwaysCount), grid
   * colliders follow; packedObjects / packedMeshes record the same order so a
   * readContactCounts() index resolves to the right entity.
   *
   * A rail's B slot (unused by the rail SDF, which reads only uRailTex) is
   * repurposed to carry its local-frame bounding circle (center xy + radius over
   * the sampled centerline). sdRail uses it as a lower-bound reject so a rail that
   * cannot beat the running nearest distance skips its sample loop — radius is
   * inflated so the bound stays conservative (never over-rejects) under fp.
   */
  private writeColliderSlot(tex: Float32Array, railData: Float32Array, k: number, i: number): boolean {
    const o = this.objects[i];
    const live = this.livePose.get(o.id);
    const pos = live ? live.pos : o.position;
    const rot = live ? live.quat : o.rotation;
    const vel = this.velocityById.get(o.id);
    const effectiveMass = o.massEnabled && o.type !== "force" ? o.mass : 0;
    const base = k * 24;
    tex[base] = pos.x; tex[base + 1] = pos.y; tex[base + 2] = pos.z; tex[base + 3] = TYPE_CODE[o.type as ColliderType];
    tex[base + 4] = o.params.x; tex[base + 5] = o.params.y; tex[base + 6] = o.params.z; tex[base + 7] = o.params.w;
    tex[base + 8] = o.restitution; tex[base + 9] = (o.arcDeg * Math.PI) / 360; tex[base + 10] = 0; tex[base + 11] = effectiveMass;
    tex[base + 12] = o.friction; tex[base + 13] = o.killOnContact ? 1 : 0; tex[base + 14] = o.fade; tex[base + 15] = o.collisionEnabled ? 1 : 0;
    tex[base + 16] = rot.x; tex[base + 17] = rot.y; tex[base + 18] = rot.z; tex[base + 19] = rot.w;
    tex[base + 20] = vel ? vel.x : 0; tex[base + 21] = vel ? vel.y : 0; tex[base + 22] = vel ? vel.z : 0; tex[base + 23] = 0;
    let railDirty = false;
    if (o.type === "rail") {
      const samples = this.railSamples(o);
      railData.set(samples, k * RAIL_SAMPLES * 4);
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (let s = 0; s < RAIL_SAMPLES; s++) {
        const sx = samples[s * 4];
        const sy = samples[s * 4 + 1];
        if (sx < minX) minX = sx;
        if (sx > maxX) maxX = sx;
        if (sy < minY) minY = sy;
        if (sy > maxY) maxY = sy;
      }
      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      let r2 = 0;
      for (let s = 0; s < RAIL_SAMPLES; s++) {
        const dx = samples[s * 4] - cx;
        const dy = samples[s * 4 + 1] - cy;
        const d2 = dx * dx + dy * dy;
        if (d2 > r2) r2 = d2;
      }
      tex[base + 4] = cx;
      tex[base + 5] = cy;
      tex[base + 6] = Math.sqrt(r2) * 1.0001 + 1e-4;
      if (this.railRowOwner[k] !== o.id) {
        this.railRowOwner[k] = o.id;
        railDirty = true;
      }
    }
    this.packedObjects.push(o);
    this.packedMeshes.push(this.meshes[i]);
    return railDirty;
  }

  /**
   * Rotation-invariant bounding radius of a collider in its own frame, the upper
   * bound used both to classify large colliders (sent to the always-test list)
   * and to expand the grid-insertion AABB. Force returns its disk radius (inert
   * — force always rides the always-test list).
   */
  private colliderRadius(o: PlacedObject): number {
    switch (o.type) {
      case "box":
        return Math.hypot(o.params.x, o.params.y, o.params.z);
      case "sphere":
        return o.params.x;
      case "funnel":
        return Math.hypot(Math.max(o.params.x, o.params.y), o.params.z) + o.params.w;
      case "force":
        return o.params.x;
      case "rail": {
        let m = 0;
        for (const p of o.railPoints) {
          const d = Math.hypot(p.x, p.y);
          if (d > m) {
            m = d;
          }
        }
        return m + railHalfGap(this.particleRadius) + railTubeRadius(this.particleRadius);
      }
      default:
        return 0;
    }
  }

  /**
   * Bins the grid-classified colliders (slots [alwaysCount, uObjCount)) into the
   * 2D XY uniform grid and bakes the CSR into uGridHeadTex / uGridIdxTex. Each
   * object is inserted into every cell its AABB — expanded by the particle
   * contact radius — overlaps, so a particle's own cell already holds every grid
   * object within contact reach (the shader scans the 3×3 neighbourhood for
   * extra fast-particle margin). The grid spans only the grid objects' AABB
   * union; the cell size grows above GRID_CELL_SIZE if that union would exceed
   * GRID_MAX_DIM cells, keeping the head texture bounded at the cost of a coarser
   * grid. Capacity never overflows: a grid collider spans ≤ GRID_LARGE_SPAN_CELLS
   * + 1 cells per axis (larger ones are always-test), so the flat list fits
   * GRID_IDX_CAP; the guard is defensive only.
   */
  private buildGrid(alwaysCount: number): void {
    const { uGridHeadTex, uGridIdxTex } = this.uniforms;
    const head = uGridHeadTex.value.image.data as Float32Array;
    const idx = uGridIdxTex.value.image.data as Float32Array;
    const n = this.gridObjIdx.length;
    const pad = this.particleRadius;

    if (n === 0) {
      head[0] = 0;
      head[1] = 0;
      this.uniforms.uGridW.value = 1;
      this.uniforms.uGridH.value = 1;
      this.uniforms.uGridOrigin.value.set(0, 0);
      this.uniforms.uCellSize.value = GRID_CELL_SIZE;
      uGridHeadTex.value.needsUpdate = true;
      return;
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let g = 0; g < n; g++) {
      const ex = this.gridRadius[g] + pad;
      minX = Math.min(minX, this.gridPosX[g] - ex);
      minY = Math.min(minY, this.gridPosY[g] - ex);
      maxX = Math.max(maxX, this.gridPosX[g] + ex);
      maxY = Math.max(maxY, this.gridPosY[g] + ex);
    }
    const cellSize = Math.max(GRID_CELL_SIZE, (maxX - minX) / GRID_MAX_DIM, (maxY - minY) / GRID_MAX_DIM);
    const gridW = Math.min(Math.max(Math.ceil((maxX - minX) / cellSize), 1), GRID_MAX_DIM);
    const gridH = Math.min(Math.max(Math.ceil((maxY - minY) / cellSize), 1), GRID_MAX_DIM);
    const cellTotal = gridW * gridH;

    const count = this.gridCellCount;
    const start = this.gridCellStart;
    const cursor = this.gridCellCursor;
    for (let c = 0; c < cellTotal; c++) {
      count[c] = 0;
    }
    for (let g = 0; g < n; g++) {
      const ex = this.gridRadius[g] + pad;
      const cx0 = Math.min(Math.max(Math.floor((this.gridPosX[g] - ex - minX) / cellSize), 0), gridW - 1);
      const cx1 = Math.min(Math.max(Math.floor((this.gridPosX[g] + ex - minX) / cellSize), 0), gridW - 1);
      const cy0 = Math.min(Math.max(Math.floor((this.gridPosY[g] - ex - minY) / cellSize), 0), gridH - 1);
      const cy1 = Math.min(Math.max(Math.floor((this.gridPosY[g] + ex - minY) / cellSize), 0), gridH - 1);
      for (let cy = cy0; cy <= cy1; cy++) {
        for (let cx = cx0; cx <= cx1; cx++) {
          count[cy * gridW + cx]++;
        }
      }
    }
    let acc = 0;
    for (let c = 0; c < cellTotal; c++) {
      start[c] = acc;
      cursor[c] = acc;
      acc += count[c];
    }
    if (acc > GRID_IDX_CAP && !this.gridIdxOverflowWarned) {
      console.warn(`ObjectField: grid index capacity (${GRID_IDX_CAP}) exceeded (${acc}); some grid colliders dropped from candidate lists`);
      this.gridIdxOverflowWarned = true;
    }
    for (let g = 0; g < n; g++) {
      const slot = alwaysCount + g;
      const ex = this.gridRadius[g] + pad;
      const cx0 = Math.min(Math.max(Math.floor((this.gridPosX[g] - ex - minX) / cellSize), 0), gridW - 1);
      const cx1 = Math.min(Math.max(Math.floor((this.gridPosX[g] + ex - minX) / cellSize), 0), gridW - 1);
      const cy0 = Math.min(Math.max(Math.floor((this.gridPosY[g] - ex - minY) / cellSize), 0), gridH - 1);
      const cy1 = Math.min(Math.max(Math.floor((this.gridPosY[g] + ex - minY) / cellSize), 0), gridH - 1);
      for (let cy = cy0; cy <= cy1; cy++) {
        for (let cx = cx0; cx <= cx1; cx++) {
          const lin = cursor[cy * gridW + cx]++;
          if (lin < GRID_IDX_CAP) {
            idx[lin * 4] = slot;
          }
        }
      }
    }
    for (let cy = 0; cy < gridH; cy++) {
      for (let cx = 0; cx < gridW; cx++) {
        const cell = cy * gridW + cx;
        const texel = (cy * GRID_MAX_DIM + cx) * 4;
        head[texel] = start[cell];
        head[texel + 1] = count[cell];
      }
    }
    this.uniforms.uGridW.value = gridW;
    this.uniforms.uGridH.value = gridH;
    this.uniforms.uGridOrigin.value.set(minX, minY);
    this.uniforms.uCellSize.value = cellSize;
    uGridHeadTex.value.needsUpdate = true;
    uGridIdxTex.value.needsUpdate = true;
  }

  /**
   * Sampled center line of a rail as texel data (x, y, 0, 0) × RAIL_SAMPLES,
   * cached per object id — control points are immutable after placement, so
   * the Catmull-Rom resampling never reruns on the per-frame repack.
   */
  private railSamples(obj: PlacedObject): Float32Array {
    let cached = this.railSampleCache.get(obj.id);
    if (!cached) {
      const pts = sampleRailCenterline(obj.railPoints);
      cached = new Float32Array(RAIL_SAMPLES * 4);
      for (let j = 0; j < pts.length; j++) {
        cached[j * 4] = pts[j].x;
        cached[j * 4 + 1] = pts[j].y;
      }
      this.railSampleCache.set(obj.id, cached);
      this.railSamplesRecomputed = true;
    }
    return cached;
  }

  /**
   * Re-runs the collision pack from outside (the per-frame transform pass calls
   * this after staging live poses / velocities for orbit-driven and dragged
   * colliders). At MAX_OBJECTS = 32 a full repack each frame is negligible.
   */
  repack(): void {
    this.pack();
  }

  /**
   * Overrides the mesh and collision pose of one object with a transform-driven
   * live pose without touching `PlacedObject.position/rotation`, which stay the
   * base pose (the source of truth). Disconnecting calls clearLivePose to snap
   * the object back to that base.
   */
  setLivePose(id: string, pos: THREE.Vector3, quat: THREE.Quaternion): void {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0) {
      return;
    }
    let entry = this.livePose.get(id);
    if (!entry) {
      entry = { pos: new THREE.Vector3(), quat: new THREE.Quaternion() };
      this.livePose.set(id, entry);
    }
    entry.pos.copy(pos);
    entry.quat.copy(quat);
    this.meshes[idx].position.copy(pos);
    this.meshes[idx].quaternion.copy(quat);
  }

  /**
   * Drives a group node's pick/wireframe box to an oriented bounding box (world center
   * + orientation + local-frame size). The box geometry is a unit cube, so size maps to
   * mesh scale; the orientation is the group's live rotation, so the box rotates with
   * the cluster under an orbit parent or a gizmo rotate (the size comes from the members'
   * AABB in the group's own pre-rotation frame). No-op for an unknown id or non-group.
   */
  setGroupBox(id: string, center: THREE.Vector3, quat: THREE.Quaternion, size: THREE.Vector3): void {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0 || this.objects[idx].type !== "group") {
      return;
    }
    const mesh = this.meshes[idx];
    mesh.visible = true;
    mesh.position.copy(center);
    mesh.quaternion.copy(quat);
    mesh.scale.set(Math.max(size.x, 1e-3), Math.max(size.y, 1e-3), Math.max(size.z, 1e-3));
  }

  /**
   * Hides a group's invisible pick box so it is neither selectable (orderedHits
   * skips non-visible meshes) nor able to host a selection halo. Used when a
   * group has no currently-visible members — e.g. a signal-node-only group while
   * the node feature is off — so the cluster is not a phantom click target.
   */
  setGroupInactive(id: string): void {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0 || this.objects[idx].type !== "group") {
      return;
    }
    this.meshes[idx].visible = false;
  }

  /**
   * Front-most widget control part under the ray, plus which parameter it drives
   * (`paramName`, empty for a button). The selection raycast (`orderedHits`)
   * tests root meshes only, so control children are invisible to it; this
   * recursive pass is the operate-side complement. A button counts its whole
   * body (base root + cap), so a click anywhere on it drives the press; a knob /
   * envelope counts only its `widgetControl`-tagged caps so the base body stays a
   * plain selection target, and the grabbed cap's `knobParam` names the target.
   * The caller compares `dist` against the merged selection hits so an object in
   * front of a widget still wins.
   */
  widgetControlHit(raycaster: THREE.Raycaster): { obj: PlacedObject; paramName: string; dist: number } | null {
    const roots: THREE.Mesh[] = [];
    for (let i = 0; i < this.objects.length; i++) {
      const type = this.objects[i].type;
      if ((type === "button" || type === "knob" || type === "envelope" || type === "array" || type === "curve" || type === "pitchQuantize") && !this.disabledTypes.has(type)) {
        roots.push(this.meshes[i]);
      }
    }
    if (roots.length === 0) {
      return null;
    }
    const hits = raycaster.intersectObjects(roots, true);
    for (const h of hits) {
      const rootIdx = this.meshes.indexOf(h.object as THREE.Mesh);
      if (rootIdx >= 0 && this.objects[rootIdx].type === "button") {
        return { obj: this.objects[rootIdx], paramName: "", dist: h.distance };
      }
      let control: THREE.Object3D | null = h.object;
      while (control !== null && !control.userData.widgetControl) {
        control = control.parent;
      }
      if (control === null) {
        continue;
      }
      const idx = this.meshes.indexOf(control.parent as THREE.Mesh);
      if (idx >= 0) {
        return { obj: this.objects[idx], paramName: (control.userData.knobParam as string) ?? "", dist: h.distance };
      }
    }
    return null;
  }

  /**
   * Flips one pitch class in a PitchQuantize node's mask (params.x) and rebuilds
   * its mesh so the key relights. Selection-independent so a tap toggles the key
   * in play mode too; the caller brackets it in an undo step and persists.
   */
  togglePitchClass(id: string, pitchClass: number): void {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0 || this.objects[idx].type !== "pitchQuantize") {
      return;
    }
    const obj = this.objects[idx];
    this.undoRecorder?.(id);
    const next = (pitchQuantizeMask(obj) ^ (1 << pitchClass)) & FULL_SCALE_MASK;
    obj.params.set(next, 0, 0, 0);
    this.rebuildMesh(idx);
  }

  /**
   * Highlights the live cursor column of an array / curve sequencer from the
   * per-frame runtime cursor. Cheap and idempotent: the array repaints its cells
   * only when the cursor column actually changes, the curve just slides its
   * marker. No-op for any other type or a missing mesh.
   */
  setSeqCursor(id: string, cursorCol: number): void {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0) {
      return;
    }
    const obj = this.objects[idx];
    const mesh = this.meshes[idx];
    if (obj.type === "array") {
      if (mesh.userData.seqCursorCol === cursorCol) {
        return;
      }
      mesh.userData.seqCursorCol = cursorCol;
      paintArrayCells(mesh, obj, cursorCol);
      return;
    }
    if (obj.type === "curve") {
      const cursor = mesh.userData.curveCursor as THREE.Mesh | undefined;
      const cols = Math.max(1, obj.seqLen);
      const w = (mesh.userData.seqW as number) ?? CURVE_WIDTH;
      if (cursor) {
        const cursorW = (cursor.geometry as THREE.PlaneGeometry).parameters.width;
        const half = Math.max(0, w / 2 - cursorW / 2);
        const raw = cols === 1 ? 0 : (cursorCol / (cols - 1) - 0.5) * w;
        cursor.position.x = Math.max(-half, Math.min(half, raw));
      }
    }
  }

  /**
   * Updates a display panel's lit lamps for its resolved `value`. Idempotent: it
   * repaints only when the value actually changes. No-op for any other type or a
   * missing mesh.
   */
  setDisplayValue(id: string, value: number): void {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0 || this.objects[idx].type !== "display") {
      return;
    }
    const mesh = this.meshes[idx];
    if (mesh.userData.displayValue === value) {
      return;
    }
    mesh.userData.displayValue = value;
    paintDisplaySegments(mesh, value);
  }

  /**
   * Records a count node's resolved (multiplayer-reconciled) readout so its CSS2D
   * label can show the same value the counter sync broadcasts, rather than the raw
   * local accumulator. Stored on the mesh so it clears with the mesh; the label
   * reads it back via countReadout.
   */
  setCountReadout(id: string, value: number): void {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0 || this.objects[idx].type !== "count") {
      return;
    }
    this.meshes[idx].userData.countReadout = value;
  }

  /** The last count readout stored by setCountReadout for the mesh at index `idx`, or undefined. */
  countReadoutAt(idx: number): number | undefined {
    return this.meshes[idx]?.userData.countReadout as number | undefined;
  }

  /** Rebuilds one entity's mesh in place (used when a type conversion changes its visual kind). */
  refreshMeshById(id: string): void {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0) {
      return;
    }
    this.rebuildMesh(idx);
  }

  /**
   * The (col,row) of an array grid under the ray, inverted from the root plane's
   * local hit point (not a per-cell raycast), or null when the ray misses. row is
   * measured from seqMin upward.
   */
  arrayCellAt(id: string, raycaster: THREE.Raycaster): { col: number; row: number } | null {
    const mesh = this.meshById(id);
    if (!mesh) {
      return null;
    }
    const hit = raycaster.intersectObject(mesh, false)[0];
    if (!hit) {
      return null;
    }
    const rows = mesh.userData.seqRows as number;
    const cols = mesh.userData.seqCols as number;
    const pitchX = (mesh.userData.seqPitchX as number) ?? SEQ_CELL_PITCH;
    const pitchY = (mesh.userData.seqPitchY as number) ?? SEQ_CELL_PITCH;
    const local = mesh.worldToLocal(hit.point.clone());
    const col = Math.min(cols - 1, Math.max(0, Math.floor((local.x + (cols * pitchX) / 2) / pitchX)));
    const row = Math.min(rows - 1, Math.max(0, Math.floor((local.y + (rows * pitchY) / 2) / pitchY)));
    return { col, row };
  }

  /** Writes one array column to the integer value at `row` (seqMin + row) and repaints, without a rebuild. */
  setArrayCell(id: string, col: number, row: number): void {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0 || this.objects[idx].type !== "array") {
      return;
    }
    const obj = this.objects[idx];
    if (col < 0 || col >= obj.seqValues.length) {
      return;
    }
    this.undoRecorder?.(id);
    obj.seqValues[col] = coerceSeqValue("array", obj.seqMin + row, obj.seqMin, obj.seqMax);
    paintArrayCells(this.meshes[idx], obj, (this.meshes[idx].userData.seqCursorCol as number) ?? 0);
  }

  /**
   * The (index,value) of a curve sample under the ray, inverted from the root
   * plane's local hit point: x → sample index, y → value mapped into [seqMin,
   * seqMax]. Null when the ray misses.
   */
  curveSampleAt(id: string, raycaster: THREE.Raycaster): { index: number; value: number } | null {
    const mesh = this.meshById(id);
    if (!mesh) {
      return null;
    }
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0 || this.objects[idx].type !== "curve") {
      return null;
    }
    const hit = raycaster.intersectObject(mesh, false)[0];
    if (!hit) {
      return null;
    }
    const obj = this.objects[idx];
    const cols = Math.max(1, obj.seqLen);
    const w = (mesh.userData.seqW as number) ?? CURVE_WIDTH;
    const h = (mesh.userData.seqH as number) ?? CURVE_HEIGHT;
    const local = mesh.worldToLocal(hit.point.clone());
    const index = Math.min(cols - 1, Math.max(0, Math.round((local.x / w + 0.5) * (cols - 1))));
    const value = coerceSeqValue("curve", (local.y / h + 0.5) * (obj.seqMax - obj.seqMin) + obj.seqMin, obj.seqMin, obj.seqMax);
    return { index, value };
  }

  /** Writes one curve sample and rewrites the polyline vertex in place, without a rebuild. */
  setCurveSample(id: string, index: number, value: number): void {
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0 || this.objects[idx].type !== "curve") {
      return;
    }
    const obj = this.objects[idx];
    if (index < 0 || index >= obj.seqValues.length) {
      return;
    }
    this.undoRecorder?.(id);
    obj.seqValues[index] = coerceSeqValue("curve", value, obj.seqMin, obj.seqMax);
    repaintCurveLine(this.meshes[idx], obj);
  }

  /** Sinks / restores a button's cap; the press reads through the drop below the rim and the darkened cap. */
  setButtonPressed(id: string, pressed: boolean): void {
    const mesh = this.meshById(id);
    if (!mesh) {
      return;
    }
    const cap = mesh.userData.buttonCap as THREE.Mesh | undefined;
    if (!cap) {
      return;
    }
    cap.position.z = (mesh.userData.capRestZ as number) - (pressed ? BUTTON_CAP_PRESS_SINK : 0);
    (cap.material as THREE.MeshStandardMaterial).color.copy(pressed ? BUTTON_CAP_PRESSED_COLOR : BUTTON_CAP_COLOR);
  }

  /**
   * Re-angles one knob subcontrol from a [0,1] value without a mesh rebuild. The
   * caller owns the underlying data (knobValue / envelope params); this only
   * spins the matching cap so the indicator tracks a drag or a driven write.
   */
  setKnobIndicator(id: string, param: string, value: number): void {
    const mesh = this.meshById(id);
    if (!mesh) {
      return;
    }
    const controls = mesh.userData.knobControls as Record<string, THREE.Object3D> | undefined;
    const cap = controls?.[param];
    if (cap) {
      setKnobAngle(cap, value);
    }
    const curve = mesh.userData.envelopeCurve as THREE.Line | undefined;
    const obj = this.objects.find((o: PlacedObject): boolean => o.id === id);
    if (curve && obj) {
      const attr = curve.geometry.getAttribute("position") as THREE.BufferAttribute;
      (attr.array as Float32Array).set(envelopeCurvePositions(obj.params));
      attr.needsUpdate = true;
    }
    const grid = mesh.userData.envelopeGrid as THREE.LineSegments | undefined;
    if (grid && obj) {
      const gattr = grid.geometry.getAttribute("position") as THREE.BufferAttribute;
      const count = envelopeGridPositions(obj.params, gattr.array as Float32Array);
      grid.geometry.setDrawRange(0, count * 2);
      gattr.needsUpdate = true;
    }
  }

  /**
   * Drives a lamp dome's emissive from the per-frame signal level in [0,1].
   * The lit color is the bloom-compensated emissive from lampEmissiveColor
   * (peak-equalized + luminance-floored) so every hue blooms at a matched
   * magnitude; level scales it through emissiveIntensity. Runs per lamp per
   * frame, so it is the authoritative writer of material.emissive.
   */
  setLampLevel(id: string, level: number): void {
    const obj = this.objects.find((o: PlacedObject): boolean => o.id === id);
    const mesh = this.meshById(id);
    if (!obj || !mesh) {
      return;
    }
    const dome = mesh.userData.lampDome as THREE.Mesh | undefined;
    if (!dome) {
      return;
    }
    const material = dome.material as THREE.MeshStandardMaterial;
    lampEmissiveColor(obj.lampHue, obj.lampSat, material.emissive);
    material.emissiveIntensity = level;
  }

  /**
   * Repaints a lamp dome from the object's current hue / saturation without a
   * rebuild — the Param-in drive calls this per written frame, so a wired hue
   * can sweep the color live. The off-state diffuse keeps the same 0.4
   * darkening the builder applies.
   */
  setLampColor(id: string): void {
    const obj = this.objects.find((o: PlacedObject): boolean => o.id === id);
    const mesh = this.meshById(id);
    if (!obj || !mesh) {
      return;
    }
    const dome = mesh.userData.lampDome as THREE.Mesh | undefined;
    if (!dome) {
      return;
    }
    const color = lampColor(obj.lampHue, obj.lampSat);
    const material = dome.material as THREE.MeshStandardMaterial;
    material.color.copy(color).multiplyScalar(0.4);
    lampEmissiveColor(obj.lampHue, obj.lampSat, material.emissive);
  }

  clearLivePose(id: string): void {
    if (!this.livePose.delete(id)) {
      return;
    }
    const idx = this.objects.findIndex((o: PlacedObject): boolean => o.id === id);
    if (idx < 0) {
      return;
    }
    this.meshes[idx].position.copy(this.objects[idx].position);
    this.meshes[idx].quaternion.copy(this.objects[idx].rotation);
  }

  liveOrBasePosition(id: string): THREE.Vector3 | null {
    const obj = this.objects.find((o: PlacedObject): boolean => o.id === id);
    if (!obj) {
      return null;
    }
    return this.livePose.get(id)?.pos ?? obj.position;
  }

  liveOrBaseRotation(id: string): THREE.Quaternion | null {
    const obj = this.objects.find((o: PlacedObject): boolean => o.id === id);
    if (!obj) {
      return null;
    }
    return this.livePose.get(id)?.quat ?? obj.rotation;
  }

  setVelocity(id: string, vel: THREE.Vector3): void {
    let entry = this.velocityById.get(id);
    if (!entry) {
      entry = new THREE.Vector3();
      this.velocityById.set(id, entry);
    }
    entry.copy(vel);
  }

  clearVelocities(): void {
    this.velocityById.clear();
  }

  /**
   * The collider subsequence in the exact order they were packed into the
   * collision uniforms, so index k aligns with `readContactCounts().counts[k]`.
   */
  packedColliders(): readonly PlacedObject[] {
    return this.packedObjects;
  }

  packedMeshAt(k: number): THREE.Mesh | null {
    return k >= 0 && k < this.packedMeshes.length ? this.packedMeshes[k] : null;
  }

  /** World-space radius of a rail's invisible pick tube — the perpendicular reach within which a ray hits the rail. */
  railPickRadius(): number {
    return railHalfGap(this.particleRadius) + railTubeRadius(this.particleRadius) + 0.2;
  }
}
